import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Plus, Trash2, X } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  HubWorkspaceBody,
  HubWorkspaceHeader,
  HubWorkspacePage,
  HubWorkspacePanel,
} from "@/components/layout/HubWorkspace";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import {
  DeferredCommitStatusGlyph,
  deferredCommitStatusGlyphVisible,
} from "@/components/mrp/DeferredCommitStatusGlyph";
import {
  deferredCommitControlClassName,
  useDeferredCommitFieldFeedback,
} from "@/components/mrp/deferredCommitFieldShared";
import { SupplierSearchableSelect } from "@/components/mrp/SupplierSearchableSelect";
import type { SupplierForSearchRow } from "@/components/mrp/supplierSearchableSelectOptions";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintTrigger, HoverHintWithGlyph, PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS,
  mapMegaventoryPaymentTermsToLabel,
  megaventoryEnumerationFromPaymentTermsUserInput,
} from "@/lib/integrator-payment-terms";
import { paymentTermsMvFromSupplier } from "@/lib/purchaseOrderDraftForm";
import { buildNavigateTargetWithTrail, readTrailFromLocation } from "@/lib/navigationTrail";
import { useNavigationTrail } from "@/lib/navigationTrailContext";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  AP_INVOICE_DRAFT_VENDOR_NUMBER,
  createApInvoiceDraft,
  deleteApInvoiceDocument,
  deleteApInvoiceDraft,
  fetchApInvoice,
  fetchApInvoiceCbpTsh,
  fetchApInvoiceDocumentById,
  fetchApInvoiceDocumentPreview,
  checkApInvoiceManufacturerParts,
  fetchApInvoiceDocumentUrl,
  fetchApInvoiceUps,
  fetchApPurchaseOrderLines,
  mergeApInvoice,
  migrateApInvoiceManufacturerBatch,
  patchApInvoice,
  reportApInvoiceAiReviewIssue,
  retryApInvoiceAiReview,
  returnApInvoiceToDraft,
  setApInvoiceUpsMode,
  splitApInvoiceIntoVendorDrafts,
  submitApInvoice,
  uploadApInvoiceDocument,
  type ApInvoiceLinePatchInput,
  type ApInvoicePurchaseKind,
  type ApInvoiceRow,
  type ApInvoiceStatus,
} from "./apInvoicesApi";
import { fetchApPurchaseOrders } from "./apApi";
import { ApInvoiceDocumentPanel } from "./ApInvoiceDocumentPanel";
import { ApInvoiceCommentsPanel } from "./ApInvoiceCommentsPanel";
import { ApInvoiceFreshdeskTicketLink } from "./ApInvoiceFreshdeskTicketLink";
import { ApInvoicePoSiblingInvoices } from "./ApInvoicePoSiblingInvoices";
import { ApInvoiceUpsPanel } from "./ApInvoiceUpsPanel";
import { ApInvoiceCbpTshPanel } from "./ApInvoiceCbpTshPanel";
import {
  ApInvoiceSplitConfirmModal,
  type ApInvoiceSplitConfirmGroup,
  type ApInvoiceSplitModalMode,
} from "./ApInvoiceSplitConfirmModal";
import {
  apInvoiceSplitSuggestionBadgeLabel,
  suggestApInvoiceDocumentGroups,
} from "./apInvoiceDocumentGroups";
import { apInvoiceManufacturerMoveNavigation } from "./apInvoiceManufacturerMoveNavigation";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { apInvoicePurchaseKindLabel } from "./apInvoicePurchaseKindUi";
import {
  apInvoiceLineFinishedGoodSku,
  apInvoiceLineHasPartNumbers,
  apInvoiceLineMpn,
  apInvoiceLineOurSku,
  apInvoiceLineShowsFinishedGoodField,
  apInvoiceLineVendorPart,
  apInvoiceSkuTranslationIsAlert,
  apInvoiceTestedSkuTranslation,
  applyManufacturerPartTestResult,
  hydrateApInvoiceExtractedPartFields,
  isManufacturingLaborSku,
  manufacturerPartInputsDifferFromTest,
  parseFinishedGoodSkuFromLaborDescription,
  testedManufacturerPartFromLoadedLine,
} from "./apInvoiceSkuTranslation";
import {
  apInvoiceWorkflowBadgeTone,
  canRetryApInvoiceAiReview,
  canReturnApInvoiceToDraftBeforeGipi,
  isApInvoiceAiReviewPolling,
  isApInvoiceDeletable,
  isApInvoiceEditable,
  isApInvoicePendingTshWorkflow,
  isApInvoiceSubmittedReview,
} from "./apInvoiceStatusUi";
import {
  ApInvoiceTshOpenMegaventoryButton,
  ApInvoiceTshParentSelector,
  ApInvoiceTshWorkflowPanel,
} from "./ApInvoiceTshWorkflowPanel";
import { ApInvoiceExpenseCategoryField } from "./ApInvoiceExpenseCategoryField";
import { ApInvoiceHeaderOverflowMenu, type ApInvoiceHeaderMenuItem } from "./ApInvoiceHeaderOverflowMenu";
import { useApInvoiceEarlyPaymentRequest } from "./apInvoiceEarlyPaymentRequest";
import { ApInvoiceMergeModal } from "./ApInvoiceMergeModal";
import {
  TSH_ONE_OFF_DESCRIPTION_HOVER_HINT,
  TSH_ONE_OFF_HOVER_HINT,
  TSH_SHIPPING_HOVER_HINT,
  TSH_TARIFF_HOVER_HINT,
  apInvoiceShowsTshWorkflow,
  canCompleteApInvoiceTshWorkflow,
} from "./apInvoiceTshUi";
import {
  AP_INVOICE_TRACKING_HOVER_HINT,
  apInvoiceTrackingChipSourceLabel,
  parseApInvoiceTrackingNumbers,
  purchaseOrderTrackingCandidatesNotOnInvoice,
  resolveApInvoiceTrackingChipPresentation,
  toggleApInvoiceTrackingNumber,
  visiblePurchaseOrderTrackingCandidates,
} from "./apInvoiceTracking";
import { useAccountingApInvoicesLiveQueries } from "@/lib/useAccountingApInvoicesLiveQueries";

const AP_INVOICES_LIST_KEY = ["accounting", "ap-invoices"] as const;

const EDIT_HELP_TEXT = [
  "Record a vendor invoice before or after Megaventory GIPI mirroring.",
  "",
  "Changes save when you leave a field (blur). Use AI review on the uploaded document to prefill fields, verify the results, then submit when vendor, invoice date, invoice number, and line items are complete.",
  "",
  "For consulting, engineering, and similar expense bills, set Classification to Non-PO purchase. Those invoices skip purchase-order linking and dock receipt matching.",
  "",
  "Receipt capture and approval happen in Shipping inbound — not on this form. Megaventory purchase invoices (GIPI) are created separately when receipt and vendor invoice quantities match; this page only records the vendor invoice and shows the link when it exists. When you link a purchase order, ",
  "the hub prefills line items from the purchase order. It does not attach the latest inbound receipt. ",
  "You can edit quantities and amounts to match the vendor invoice. Log tariff, shipping, and one-off fees in the TSH fields; those become separate Megaventory TSH service lines (tariff, shipping-d or shipping-i, fee), not GIPI stock lines. Tracking is optional; if the purchase order already has tracking from Incoming Parts or dock receiving, pick the number that belongs on this invoice.",
].join(" ");

const PO_LINK_HELP =
  "Linking a purchase order prefills line items from that order. It does not attach the latest inbound receipt. Not used for Non-PO purchase invoices.";

const PURCHASE_KIND_HELP =
  "PO / inventory expects a dock receipt and GIPI match. Non-PO purchase is for consulting, engineering, and other expenses that should not wait on a receipt. Standalone TSH is a second vendor invoice for tariff, shipping, and fees only (for example a duty or freight bill from a carrier or broker). Link it to the specific GIPI those charges belong to. Multi-PO TSH is one carrier bill, such as a UPS or DHL invoice. Freight to us can attach to a purchase order. Freight from us to a customer, an RMA, or a return stays on this invoice as a non-PO expense.";

const STANDALONE_TSH_GIPI_HELP =
  "Search by GIPI number, purchase order, or the stock vendor on that GIPI. The carrier or broker on this bill can be a different supplier, for example DHL charges on a Beijing Yuantang GIPI. A purchase order is not required on this invoice, because an order can have more than one shipment.";

function purchaseOrderIdForApInvoiceForm(invoice: {
  purchase_kind?: string | null;
  purchase_order_id?: string | null;
}): string {
  if (invoice.purchase_kind === "standalone_tsh") {
    return "";
  }
  return invoice.purchase_order_id ?? "";
}

const PAYMENT_TERMS_HELP =
  "Defaults from the vendor (or from the linked PO for PO / inventory). Change when the invoice Terms box differs, for example NT60 on a duty bill.";

/** Stored value wins; otherwise fall back to the linked PO (PO / inventory) and then the vendor default. */
function resolveApInvoicePaymentTermsForForm(invoice: ApInvoiceRow): string {
  const stored = invoice.payment_terms?.trim() ?? "";
  if (stored !== "") {
    return megaventoryEnumerationFromPaymentTermsUserInput(stored, FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS);
  }
  if (invoice.purchase_kind === "po_inventory") {
    const fromPo = invoice.purchase_order_payment_terms?.trim() ?? "";
    if (fromPo !== "") {
      return megaventoryEnumerationFromPaymentTermsUserInput(fromPo, FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS);
    }
  }
  return megaventoryEnumerationFromPaymentTermsUserInput(
    paymentTermsMvFromSupplier(invoice.supplier_payment_terms),
    FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS,
  );
}

const LINE_DESCRIPTION_CLASS =
  "min-h-[8rem] w-full flex-1 resize-y rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100 disabled:text-slate-500 md:min-h-0";

const LINE_OUR_SKU_HELP = "Our stock keeping unit (Smart Grid part number).";
const LINE_FINISHED_GOOD_SKU_HELP =
  "Finished good or assembly stock keeping unit built on this contract-manufacturer line. Megaventory purchase invoices bill the labor stock keeping unit while work orders and receiving link to this finished good.";
const LINE_MPN_HELP = "Manufacturer part number from the vendor invoice or label.";
const LINE_VENDOR_PART_HELP =
  "Distributor or vendor catalog stock number (for example McMaster or Mouser stock number).";

type EditableLine = {
  clientKey: string;
  purchase_order_item_id: number | null;
  sku: string;
  description: string;
  quantity: string;
  unit_price: string;
  extracted_our_part_number: string | null;
  extracted_mpn: string | null;
  extracted_vendor_part_number: string | null;
  extracted_part_number: string | null;
  finished_good_sku: string | null;
  tested_mpn: string | null;
  tested_sku: string | null;
  sku_match_method: string | null;
  qbo_expense_account_id: string | null;
  qbo_expense_account_name: string | null;
};

function mapSavedLineToEditable(line: {
  id?: string;
  purchase_order_item_id: string | null;
  sku: string;
  description: string | null;
  quantity: string;
  unit_price: string | null;
  extracted_our_part_number?: string | null;
  extracted_mpn?: string | null;
  extracted_vendor_part_number?: string | null;
  extracted_part_number?: string | null;
  finished_good_sku?: string | null;
  sku_match_method?: string | null;
  qbo_expense_account_id?: string | null;
  qbo_expense_account_name?: string | null;
}, clientKey: string, purchaseOrderItemId?: number | null): EditableLine {
  const extracted = hydrateApInvoiceExtractedPartFields({
    sku: line.sku,
    description: line.description,
    extracted_our_part_number: line.extracted_our_part_number,
    extracted_mpn: line.extracted_mpn,
    extracted_vendor_part_number: line.extracted_vendor_part_number,
    extracted_part_number: line.extracted_part_number,
    finished_good_sku: line.finished_good_sku,
  });
  return {
    clientKey,
    purchase_order_item_id:
      purchaseOrderItemId ??
      (line.purchase_order_item_id != null ? Number.parseInt(line.purchase_order_item_id, 10) : null),
    sku: line.sku,
    description: line.description ?? "",
    quantity: line.quantity,
    unit_price: line.unit_price ?? "",
    ...extracted,
    ...testedManufacturerPartFromLoadedLine({
      sku: line.sku,
      extracted_our_part_number: extracted.extracted_our_part_number,
      extracted_mpn: extracted.extracted_mpn,
      sku_match_method: line.sku_match_method,
    }),
    qbo_expense_account_id: line.qbo_expense_account_id ?? null,
    qbo_expense_account_name: line.qbo_expense_account_name ?? null,
  };
}

function newBlankLine(): EditableLine {
  return {
    clientKey: crypto.randomUUID(),
    purchase_order_item_id: null,
    sku: "",
    description: "",
    quantity: "",
    unit_price: "",
    extracted_our_part_number: null,
    extracted_mpn: null,
    extracted_vendor_part_number: null,
    extracted_part_number: null,
    finished_good_sku: null,
    tested_mpn: null,
    tested_sku: null,
    sku_match_method: null,
    qbo_expense_account_id: null,
    qbo_expense_account_name: null,
  };
}

function parseUnitPrice(raw: string | null | undefined): number | null {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function parseOptionalTshAmount(raw: string | null | undefined): number | null {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function parseOptionalInvoiceTotal(raw: string | null | undefined): number | null {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function displayTshAmount(raw: string | null | undefined): string {
  const parsed = parseOptionalTshAmount(raw ?? "");
  return parsed == null ? "" : String(parsed);
}

function parseQuantity(raw: string | null | undefined): number | null {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function lineTotalDisplay(quantityRaw: string, unitPriceRaw: string): string {
  const quantity = parseQuantity(quantityRaw);
  const unitPrice = parseUnitPrice(unitPriceRaw);
  if (quantity == null || unitPrice == null) return "—";
  return (quantity * unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function toPatchLineInput(line: EditableLine): ApInvoiceLinePatchInput {
  const ourSku = apInvoiceLineOurSku(line);
  const mpn = apInvoiceLineMpn(line);
  const vendorPart = apInvoiceLineVendorPart(line);
  const finishedGoodSku = apInvoiceLineFinishedGoodSku(line);
  const quantity = parseQuantity(line.quantity) ?? 0;
  const persistedSku = ourSku !== "" ? ourSku : mpn !== "" ? mpn : vendorPart;
  return {
    purchase_order_item_id: line.purchase_order_item_id,
    sku: persistedSku,
    description: (line.description ?? "").trim() !== "" ? (line.description ?? "").trim() : null,
    quantity,
    unit_price: parseUnitPrice(line.unit_price),
    extracted_our_part_number: line.extracted_our_part_number,
    extracted_mpn: mpn !== "" ? mpn : null,
    extracted_vendor_part_number: vendorPart !== "" ? vendorPart : null,
    extracted_part_number: mpn !== "" ? mpn : vendorPart !== "" ? vendorPart : null,
    finished_good_sku: finishedGoodSku !== "" ? finishedGoodSku : null,
    sku_match_method: line.sku_match_method,
  };
}

function toExpensePatchLineInput(line: EditableLine): ApInvoiceLinePatchInput {
  const description = (line.description ?? "").trim();
  const amount = parseUnitPrice(line.unit_price) ?? 0;
  return {
    sku: "(expense)",
    description: description !== "" ? description : null,
    quantity: 1,
    unit_price: amount,
    line_total: amount,
    qbo_expense_account_id: line.qbo_expense_account_id,
    qbo_expense_account_name: line.qbo_expense_account_name,
  };
}

function displayVendorInvoiceNumber(raw: string): string {
  return raw === AP_INVOICE_DRAFT_VENDOR_NUMBER ? "" : raw;
}

function formatApInvoiceError(error: unknown): string {
  const raw =
    error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
  if (raw === "vendor_invoice_number is required." || raw === "vendor_invoice_number cannot be empty.") {
    return "Vendor invoice number is required.";
  }
  return raw;
}

function ApInvoiceDetailFieldLabel({
  htmlFor,
  label,
  hoverHint,
  save,
}: {
  htmlFor?: string;
  label: string;
  hoverHint?: string;
  save?: ReactNode;
}) {
  return (
    <div className="mb-1.5 flex min-h-5 items-center gap-1">
      <Label className="mb-0" htmlFor={htmlFor}>
        {label}
      </Label>
      {hoverHint ? <HoverHintWithGlyph hoverHint={hoverHint} /> : null}
      {save}
    </div>
  );
}

type ApFieldSavePhase = "dirty" | "saving" | "saved" | "error";

function ApInvoiceFieldSaveFrame({
  fieldKey,
  label,
  hoverHint,
  phase,
  error,
  children,
}: {
  fieldKey: string;
  label: string;
  hoverHint?: string;
  phase?: ApFieldSavePhase;
  error?: string | null;
  children: ReactNode;
}) {
  const isDirty = phase === "dirty";
  const pending = phase === "saving";
  const commitError = phase === "error" ? (error ?? "Could not save.") : null;
  const flashTone = useDeferredCommitFieldFeedback(phase === "saved", commitError, {
    feedbackId: fieldKey,
  });
  const showGlyph = deferredCommitStatusGlyphVisible({ pending, isDirty, commitError });
  return (
    <div className="min-w-0">
      <ApInvoiceDetailFieldLabel label={label} hoverHint={hoverHint} />
      <div className="relative min-w-0">
        <div
          className={cn(
            "rounded-md",
            deferredCommitControlClassName({
              isDirty,
              disabled: false,
              pending,
              commitError,
              flashTone,
            }),
          )}
        >
          {children}
        </div>
        {showGlyph ? (
          <div className="pointer-events-none absolute right-1 top-1.5 z-10">
            <div className="pointer-events-auto">
              <DeferredCommitStatusGlyph pending={pending} isDirty={isDirty} commitError={commitError} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ApInvoiceNewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    createApInvoiceDraft()
      .then((invoice) => {
        if (!cancelled) {
          queryClient.setQueryData(["accounting", "ap-invoice", invoice.id], invoice);
          const trail = readTrailFromLocation(location.search, location.state, new Map());
          const target = buildNavigateTargetWithTrail({
            pathname: `/accounting/ap/${encodeURIComponent(invoice.id)}/edit`,
            baseSearch: "",
            trail,
          });
          navigate(
            { pathname: target.pathname, search: target.search },
            { replace: true, state: target.state },
          );
        }
      })
      .catch((cause) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : String(cause));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [location.search, location.state, navigate, queryClient]);

  return (
    <div className="flex items-center gap-2 py-12 text-sm text-slate-600">
      {error != null ? (
        <>
          <span className="text-rose-700">{error}</span>
          <Link to="/accounting/ap" className="text-brand-700 hover:underline">
            Back to vendor invoices
          </Link>
        </>
      ) : (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Starting draft invoice…
        </>
      )}
    </div>
  );
}

export function ApInvoiceEditPage() {
  const { invoiceId = "" } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { resetNavigationTrail } = useNavigationTrail();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  useAccountingApInvoicesLiveQueries(invoiceId !== "");
  const earlyPayment = useApInvoiceEarlyPaymentRequest(invoiceId);

  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState("");
  const [statedInvoiceTotal, setStatedInvoiceTotal] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("None");
  const [notes, setNotes] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [purchaseKind, setPurchaseKind] = useState<ApInvoicePurchaseKind>("po_inventory");
  const upsQuery = useQuery({
    queryKey: ["accounting", "ap-invoice-ups", invoiceId],
    queryFn: () => fetchApInvoiceUps(invoiceId),
    enabled: invoiceId !== "",
  });
  const upsEnabled = upsQuery.data?.enabled === true;
  const [purchaseOrderId, setPurchaseOrderId] = useState("");
  const [poSearch, setPoSearch] = useState("");
  const [tshTariffAmount, setTshTariffAmount] = useState("");
  const [tshShippingAmount, setTshShippingAmount] = useState("");
  const [tshOneOffAmount, setTshOneOffAmount] = useState("");
  const [tshFeeDescription, setTshFeeDescription] = useState("");
  const [trackingNumbers, setTrackingNumbers] = useState<string[]>([]);
  const [trackingDraft, setTrackingDraft] = useState("");
  const [lines, setLines] = useState<EditableLine[]>([]);
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const manufacturerPartTestGenerationRef = useRef(0);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [poCurrency, setPoCurrency] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [hydrated, setHydrated] = useState(false);
  const hydratedInvoiceIdRef = useRef<string | null>(null);
  const leftDeletedInvoiceRef = useRef(false);
  const [fieldSave, setFieldSave] = useState<Record<string, { phase: ApFieldSavePhase; error?: string }>>({});
  const [serverPreviewUrl, setServerPreviewUrl] = useState<string | null>(null);
  const [serverFilename, setServerFilename] = useState<string | null>(null);
  const [serverContentType, setServerContentType] = useState<string | null>(null);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const multiPoTshSubmitRef = useRef<(() => Promise<ApInvoiceRow>) | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [aiIssueReportOpen, setAiIssueReportOpen] = useState(false);
  const [aiIssueReportText, setAiIssueReportText] = useState("");
  const [aiIssueReportSaved, setAiIssueReportSaved] = useState(false);
  const [returnToDraftConfirmOpen, setReturnToDraftConfirmOpen] = useState(false);
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [splitModalMode, setSplitModalMode] = useState<ApInvoiceSplitModalMode>("vendor");
  const [splitDialogError, setSplitDialogError] = useState<string | null>(null);
  const [splitBusy, setSplitBusy] = useState(false);

  const poPrefillTokenRef = useRef<string | null>(null);
  const appliedAiReviewAtRef = useRef<string | null>(null);
  const persistChainRef = useRef(Promise.resolve(true));
  const paymentTermsUserEditedRef = useRef(false);
  const prevSupplierIdForTermsRef = useRef("");

  const paymentTermLabelsQuery = useQuery({
    queryKey: ["mrp", "megaventory-payment-term-labels"],
    queryFn: () =>
      apiFetch<{ rows: { sb_enumeration: string; display_label: string }[] }>(
        "/api/mrp/megaventory-payment-term-labels",
      ),
    staleTime: 300_000,
  });

  const enumerationToLabel = useMemo(() => {
    const rows = paymentTermLabelsQuery.data?.rows;
    if (!rows?.length) {
      return FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS;
    }
    const map: Record<string, string> = {};
    for (const row of rows) {
      map[row.sb_enumeration] = row.display_label;
    }
    return map;
  }, [paymentTermLabelsQuery.data?.rows]);

  const invoiceQuery = useQuery({
    queryKey: ["accounting", "ap-invoice", invoiceId],
    queryFn: () => fetchApInvoice(invoiceId),
    enabled: invoiceId !== "" && !splitBusy,
    staleTime: 5_000,
    refetchInterval: (query) =>
      isApInvoiceAiReviewPolling(query.state.data?.status ?? "draft") ? 2_000 : false,
  });
  const toastedAiReviewErrorRef = useRef<string | null>(null);
  const toastedEarlyPaymentErrorRef = useRef<string | null>(null);

  useEffect(() => {
    const message = invoiceQuery.data?.ai_review_error;
    if (message == null || message === "") return;
    if (toastedAiReviewErrorRef.current === message) return;
    toastedAiReviewErrorRef.current = message;
    showToast({ tone: "error", message: `AI review failed: ${message}` });
  }, [invoiceQuery.data?.ai_review_error, showToast]);

  useEffect(() => {
    const message = earlyPayment.errorMessage;
    if (message == null || message === "") return;
    if (toastedEarlyPaymentErrorRef.current === message) return;
    toastedEarlyPaymentErrorRef.current = message;
    showToast({ tone: "error", message });
  }, [earlyPayment.errorMessage, showToast]);

  const selectedSupplierQuery = useQuery({
    queryKey: ["mrp", "suppliers-for-purchase-order", "ap-invoice", supplierId],
    queryFn: async () => {
      const response = await apiFetch<{ rows: SupplierForSearchRow[] }>("/api/mrp/suppliers-for-purchase-order", {
        query: { q: supplierId },
      });
      return response.rows.find((row) => String(row.id) === supplierId) ?? null;
    },
    enabled: supplierId !== "",
    staleTime: 60_000,
  });

  const poQuery = useQuery({
    queryKey: ["accounting", "ap-po-pick", supplierId, poSearch],
    queryFn: () => fetchApPurchaseOrders(poSearch.trim() !== "" ? poSearch : null, supplierId),
    enabled: supplierId !== "" && purchaseKind === "po_inventory",
    staleTime: 30_000,
  });

  const poLinesQuery = useQuery({
    queryKey: ["accounting", "ap-po-lines", purchaseOrderId],
    queryFn: () => fetchApPurchaseOrderLines(purchaseOrderId),
    enabled: purchaseOrderId !== "",
    staleTime: 30_000,
  });

  const extractedTracking = parseApInvoiceTrackingNumbers(
    invoiceQuery.data?.extracted_tracking_numbers ?? [],
  );
  const purchaseOrderTracking = visiblePurchaseOrderTrackingCandidates(
    poLinesQuery.data?.tracking ?? invoiceQuery.data?.purchase_order_tracking ?? [],
  );
  const purchaseOrderTrackingAvailable = purchaseOrderTrackingCandidatesNotOnInvoice(
    purchaseOrderTracking,
    trackingNumbers,
  );

  const navigateToApInvoiceList = useCallback(() => {
    resetNavigationTrail();
    navigate("/accounting/ap", { replace: true });
  }, [navigate, resetNavigationTrail]);

  useEffect(() => {
    if (splitBusy || !invoiceQuery.isError) return;
    const message = invoiceQuery.error instanceof Error ? invoiceQuery.error.message : "";
    if (message !== "Invoice not found." || leftDeletedInvoiceRef.current) return;
    leftDeletedInvoiceRef.current = true;
    navigateToApInvoiceList();
  }, [invoiceQuery.error, invoiceQuery.isError, navigateToApInvoiceList, splitBusy]);

  const handleInvoiceUpdated = useCallback(
    (invoice: ApInvoiceRow) => {
      const previous = queryClient.getQueryData<ApInvoiceRow>(["accounting", "ap-invoice", invoiceId]);
      queryClient.setQueryData(["accounting", "ap-invoice", invoiceId], invoice);
      if (invoice.status === "submitted" && previous?.status !== "submitted") {
        void queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
        navigateToApInvoiceList();
      }
    },
    [invoiceId, navigateToApInvoiceList, queryClient],
  );

  const upsModeMutation = useMutation({
    mutationFn: (enabled: boolean) => setApInvoiceUpsMode(invoiceId, enabled),
    onSuccess: (result) => {
      queryClient.setQueryData(["accounting", "ap-invoice-ups", invoiceId], result.ups);
      if (result.ups.enabled) {
        queryClient.setQueryData(["accounting", "ap-invoice", invoiceId], result.invoice);
        setPurchaseKind(result.invoice.purchase_kind ?? "non_po_purchase");
        setPurchaseOrderId("");
        setPoCurrency(null);
        setTshTariffAmount("");
        setTshShippingAmount("");
        setTshOneOffAmount("");
        setTshFeeDescription("");
        return;
      }
      queryClient.setQueryData<ApInvoiceRow>(["accounting", "ap-invoice", invoiceId], (current) => {
        const savedKind = draftRef.current.purchaseKind;
        const savedPurchaseOrderId =
          draftRef.current.purchaseOrderId === "" ? null : draftRef.current.purchaseOrderId;
        if (current == null) {
          return { ...result.invoice, purchase_kind: savedKind, purchase_order_id: savedPurchaseOrderId };
        }
        return {
          ...current,
          ...result.invoice,
          purchase_kind: savedKind,
          purchase_kind_label: current.purchase_kind === savedKind ? current.purchase_kind_label : result.invoice.purchase_kind_label,
          purchase_order_id: savedPurchaseOrderId,
        };
      });
    },
    onError: (error) => showToast({ tone: "error", message: formatApInvoiceError(error) }),
  });

  useEffect(() => {
    if (hydratedInvoiceIdRef.current === invoiceId) return;
    if (hydratedInvoiceIdRef.current != null) {
      setHydrated(false);
      setInvoiceFile(null);
      setServerPreviewUrl(null);
      setServerFilename(null);
      setServerContentType(null);
      setEmailPreviewHtml(null);
      setSelectedDocumentId(null);
      setFieldSave({});
    }
    hydratedInvoiceIdRef.current = invoiceId;
  }, [invoiceId]);

  useEffect(() => {
    const invoice = invoiceQuery.data;
    if (!invoice || invoice.id !== invoiceId || hydrated) return;

    setPaymentTerms(resolveApInvoicePaymentTermsForForm(invoice));
    paymentTermsUserEditedRef.current = false;
    prevSupplierIdForTermsRef.current = invoice.supplier_id ?? "";

    if (invoice.status === "pending_tsh" || invoice.status === "submitted") {
      setVendorInvoiceNumber(displayVendorInvoiceNumber(invoice.vendor_invoice_number));
      setStatedInvoiceTotal(invoice.total_amount ?? "");
      setInvoiceDate(invoice.invoice_date ?? "");
      setNotes(invoice.notes ?? "");
      setSupplierId(invoice.supplier_id ?? "");
      setPurchaseKind(invoice.purchase_kind ?? "po_inventory");
      setPurchaseOrderId(purchaseOrderIdForApInvoiceForm(invoice));
      setPoCurrency(invoice.currency);
      setTshTariffAmount(displayTshAmount(invoice.tsh_tariff_amount));
      setTshShippingAmount(displayTshAmount(invoice.tsh_shipping_amount));
      setTshOneOffAmount(displayTshAmount(invoice.tsh_one_off_amount ?? invoice.tsh_handling_amount));
      setTshFeeDescription(invoice.tsh_fee_description ?? "");
      setTrackingNumbers(parseApInvoiceTrackingNumbers(invoice.tracking_numbers ?? invoice.tracking_number));
      setLines(
        invoice.lines.map((line) => mapSavedLineToEditable(line, `saved-${line.id}`)),
      );
      setServerFilename(invoice.invoice_file_filename);
      setSelectedDocumentId(invoice.documents?.[0]?.id ?? null);
      appliedAiReviewAtRef.current = invoice.ai_review_completed_at;
    setHydrated(true);
    return;
    }

    setVendorInvoiceNumber(displayVendorInvoiceNumber(invoice.vendor_invoice_number));
    setStatedInvoiceTotal(invoice.total_amount ?? "");
    setInvoiceDate(invoice.invoice_date ?? "");
    setNotes(invoice.notes ?? "");
    setSupplierId(invoice.supplier_id ?? "");
    setPurchaseKind(invoice.purchase_kind ?? "po_inventory");
    setPurchaseOrderId(purchaseOrderIdForApInvoiceForm(invoice));
    setPoCurrency(invoice.currency);
    setTshTariffAmount(displayTshAmount(invoice.tsh_tariff_amount));
    setTshShippingAmount(displayTshAmount(invoice.tsh_shipping_amount));
    setTshOneOffAmount(displayTshAmount(invoice.tsh_one_off_amount ?? invoice.tsh_handling_amount));
    setTshFeeDescription(invoice.tsh_fee_description ?? "");
    setTrackingNumbers(parseApInvoiceTrackingNumbers(invoice.tracking_numbers ?? invoice.tracking_number));
    setTrackingDraft("");
    setLines(
      invoice.lines.length > 0
        ? invoice.lines.map((line) => mapSavedLineToEditable(line, `saved-${line.id}`))
        : [],
    );
    setServerFilename(invoice.invoice_file_filename);
    setSelectedDocumentId(invoice.documents?.[0]?.id ?? null);
    appliedAiReviewAtRef.current = invoice.ai_review_completed_at;
    setHydrated(true);
  }, [hydrated, invoiceQuery.data, navigateToApInvoiceList]);

  const workflowStatus: ApInvoiceStatus = invoiceQuery.data?.status ?? "draft";
  const pendingTshWorkflow = isApInvoicePendingTshWorkflow(workflowStatus);
  const submittedReview = isApInvoiceSubmittedReview(workflowStatus);
  const formLocked = workflowStatus === "under_ai_review" || pendingTshWorkflow || submittedReview;
  const testManufacturerParts = useCallback(() => {
    if (invoiceId === "" || formLocked) return;
    const pending = linesRef.current.filter((line) => manufacturerPartInputsDifferFromTest(line));
    if (pending.length === 0) return;
    const generation = manufacturerPartTestGenerationRef.current + 1;
    manufacturerPartTestGenerationRef.current = generation;
    const sent = pending.map((line) => ({
      clientKey: line.clientKey,
      sku: apInvoiceLineOurSku(line),
      mpn: apInvoiceLineMpn(line),
      description: (line.description ?? "").trim() === "" ? null : (line.description ?? "").trim(),
      vendorPart: apInvoiceLineVendorPart(line),
    }));
    void checkApInvoiceManufacturerParts(
      invoiceId,
      sent.map((line) => ({
        client_key: line.clientKey,
        sku: line.sku,
        description: line.description,
        extracted_our_part_number: line.sku !== "" ? line.sku : null,
        extracted_mpn: line.mpn !== "" ? line.mpn : null,
        extracted_vendor_part_number: line.vendorPart !== "" ? line.vendorPart : null,
        extracted_part_number: line.mpn !== "" ? line.mpn : null,
      })),
    )
      .then((results) => {
        if (manufacturerPartTestGenerationRef.current !== generation) return;
        const byKey = new Map(results.map((result) => [result.client_key, result]));
        setLines((current) =>
          current.map((line) => {
            const snapshot = sent.find((item) => item.clientKey === line.clientKey);
            const result = byKey.get(line.clientKey);
            if (snapshot == null || result == null) return line;
            return applyManufacturerPartTestResult(line, {
              sku: snapshot.sku,
              mpn: snapshot.mpn,
              matchMethod: result.sku_match_method,
            });
          }),
        );
      })
      .catch(() => undefined);
  }, [formLocked, invoiceId]);
  const canEdit = isApInvoiceEditable(workflowStatus);
  const draftTshTotal =
    (parseOptionalTshAmount(tshTariffAmount) ?? 0) +
    (parseOptionalTshAmount(tshShippingAmount) ?? 0) +
    (parseOptionalTshAmount(tshOneOffAmount) ?? 0);
  const showOneOffFeeDescription =
    parseOptionalTshAmount(tshOneOffAmount) != null || tshFeeDescription.trim() !== "";
  const draftStockLineCount = lines.filter((line) => apInvoiceLineHasPartNumbers(line)).length;
  const showStandaloneParentSelector =
    canEdit &&
    purchaseOrderId !== "" &&
    draftTshTotal > 0 &&
    draftStockLineCount < 1 &&
    invoiceQuery.data?.tsh_parent_purchase_invoice_id == null;

  useEffect(() => {
    const invoice = invoiceQuery.data;
    if (!invoice || !hydrated || invoice.status !== "awaiting_human_review" || invoice.lines.length === 0) return;
    const reviewCompletedAt = invoice.ai_review_completed_at;
    if (reviewCompletedAt == null || reviewCompletedAt === appliedAiReviewAtRef.current) return;
    appliedAiReviewAtRef.current = reviewCompletedAt;
    setVendorInvoiceNumber(displayVendorInvoiceNumber(invoice.vendor_invoice_number));
    setStatedInvoiceTotal(invoice.total_amount ?? "");
    setInvoiceDate(invoice.invoice_date ?? "");
    setNotes(invoice.notes ?? "");
    setSupplierId(invoice.supplier_id ?? "");
    setPurchaseKind(invoice.purchase_kind ?? "po_inventory");
    setPurchaseOrderId(purchaseOrderIdForApInvoiceForm(invoice));
    setPoCurrency(invoice.currency);
    setTshTariffAmount(displayTshAmount(invoice.tsh_tariff_amount));
    setTshShippingAmount(displayTshAmount(invoice.tsh_shipping_amount));
    setTshOneOffAmount(displayTshAmount(invoice.tsh_one_off_amount ?? invoice.tsh_handling_amount));
    setTshFeeDescription(invoice.tsh_fee_description ?? "");
    setTrackingNumbers(parseApInvoiceTrackingNumbers(invoice.tracking_numbers ?? invoice.tracking_number));
    setTrackingDraft("");
    setLines(invoice.lines.map((line) => mapSavedLineToEditable(line, `saved-${line.id}`)));
  }, [hydrated, invoiceQuery.data]);

  const storedDocuments = invoiceQuery.data?.documents;
  const documents = storedDocuments ?? [];

  const documentGroupSuggestion = useMemo(
    () =>
      suggestApInvoiceDocumentGroups(
        documents.map((document) => ({
          id: document.id,
          filename: document.filename,
          kind: document.kind,
        })),
      ),
    [documents],
  );

  const splitSuggestionBadge = useMemo(
    () => apInvoiceSplitSuggestionBadgeLabel(documentGroupSuggestion.suggested_invoice_count, documents.length),
    [documentGroupSuggestion.suggested_invoice_count, documents.length],
  );

  useEffect(() => {
    if (documents.length === 0) {
      if (selectedDocumentId != null) setSelectedDocumentId(null);
      return;
    }
    if (selectedDocumentId != null && documents.some((document) => document.id === selectedDocumentId)) {
      return;
    }
    setSelectedDocumentId(documents[0]?.id ?? null);
  }, [documents, selectedDocumentId]);

  useEffect(() => {
    if (!hydrated || invoiceId === "") return;
    if (selectedDocumentId == null) {
      if (invoiceQuery.data?.invoice_file_filename == null) return;
      let cancelled = false;
      fetchApInvoiceDocumentUrl(invoiceId)
        .then(({ signedUrl, filename }) => {
          if (!cancelled) {
            setServerPreviewUrl(signedUrl);
            setServerFilename(filename);
            setServerContentType(null);
          }
        })
        .catch(() => undefined);
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    fetchApInvoiceDocumentById(invoiceId, selectedDocumentId)
      .then(({ signedUrl, filename, contentType }) => {
        if (!cancelled) {
          setServerPreviewUrl(signedUrl);
          setServerFilename(filename);
          setServerContentType(contentType ?? null);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [hydrated, invoiceId, selectedDocumentId, invoiceQuery.data?.invoice_file_filename]);

  useEffect(() => {
    if (!hydrated || invoiceId === "" || selectedDocumentId == null) {
      setEmailPreviewHtml(null);
      return;
    }
    const selected = documents.find((document) => document.id === selectedDocumentId);
    const looksLikeEmail =
      selected?.kind === "email" ||
      (selected?.content_type ?? "").toLowerCase() === "text/html" ||
      (selected?.filename ?? "").toLowerCase().endsWith(".html");
    if (!looksLikeEmail) {
      setEmailPreviewHtml(null);
      return;
    }
    let cancelled = false;
    fetchApInvoiceDocumentPreview(invoiceId, selectedDocumentId)
      .then((payload) => {
        if (!cancelled) setEmailPreviewHtml(payload.previewHtml);
      })
      .catch(() => {
        if (!cancelled) setEmailPreviewHtml(null);
      });
    return () => {
      cancelled = true;
    };
  }, [documents, hydrated, invoiceId, selectedDocumentId]);

  useEffect(() => {
    if (purchaseKind === "standalone_tsh") return;
    if (purchaseOrderId === "") return;
    if (poPrefillTokenRef.current === purchaseOrderId) return;
    if (lines.length > 0) {
      poPrefillTokenRef.current = purchaseOrderId;
      return;
    }
    const payload = poLinesQuery.data;
    if (!payload) return;
    poPrefillTokenRef.current = purchaseOrderId;
    if (payload.supplier_id != null && payload.supplier_id !== "") {
      setSupplierId(payload.supplier_id);
    }
    setPoCurrency(payload.currency);
    setLines(
      payload.lines.map((line) =>
        mapSavedLineToEditable(
          {
            purchase_order_item_id: line.id,
            sku: line.sku,
            finished_good_sku: line.finished_good_sku ?? null,
            description: line.description,
            quantity: line.quantity,
            unit_price: line.unit_price,
          },
          `po-${line.id}`,
          Number.parseInt(line.id, 10),
        ),
      ),
    );
  }, [lines.length, purchaseKind, purchaseOrderId, poLinesQuery.data]);

  const selectedSupplier = selectedSupplierQuery.data;

  useEffect(() => {
    if (!hydrated || paymentTermsUserEditedRef.current) {
      return;
    }
    if (supplierId === prevSupplierIdForTermsRef.current) {
      return;
    }
    prevSupplierIdForTermsRef.current = supplierId;
    if (supplierId === "" || selectedSupplier == null) {
      return;
    }
    if (purchaseKind === "po_inventory" && purchaseOrderId !== "") {
      return;
    }
    setPaymentTerms(
      megaventoryEnumerationFromPaymentTermsUserInput(
        paymentTermsMvFromSupplier(selectedSupplier.payment_terms),
        enumerationToLabel,
      ),
    );
  }, [hydrated, supplierId, selectedSupplier, purchaseKind, purchaseOrderId, enumerationToLabel]);

  const supplierExtraRows = useMemo((): SupplierForSearchRow[] => {
    if (selectedSupplier != null) {
      return [selectedSupplier];
    }
    const invoiceSupplierName =
      invoiceQuery.data?.supplier_id === supplierId
        ? invoiceQuery.data?.supplier_name?.trim()
        : "";
    const prefilledName =
      invoiceSupplierName != null && invoiceSupplierName !== ""
        ? invoiceSupplierName
        : poLinesQuery.data?.supplier_name?.trim();
    if (supplierId === "" || prefilledName == null || prefilledName === "") {
      return [];
    }
    const id = Number.parseInt(supplierId, 10);
    if (!Number.isFinite(id)) {
      return [];
    }
    return [{ id, mv_supplier_id: id, mv_supplier_name: prefilledName }];
  }, [
    selectedSupplier,
    supplierId,
    poLinesQuery.data?.supplier_name,
    invoiceQuery.data?.supplier_id,
    invoiceQuery.data?.supplier_name,
  ]);

  const supplierCurrency = useMemo(() => {
    if (poCurrency != null && purchaseOrderId !== "") return poCurrency;
    const raw = selectedSupplier?.currency?.trim();
    return raw != null && raw !== "" ? raw.toUpperCase() : "USD";
  }, [poCurrency, purchaseOrderId, selectedSupplier?.currency]);

  const supplierLabelOverride = useMemo(() => {
    if (supplierId === "") return null;
    if (selectedSupplier?.mv_supplier_name?.trim()) return null;
    const invoiceSupplierName =
      invoiceQuery.data?.supplier_id === supplierId
        ? invoiceQuery.data?.supplier_name?.trim()
        : "";
    const prefilledName =
      invoiceSupplierName != null && invoiceSupplierName !== ""
        ? invoiceSupplierName
        : poLinesQuery.data?.supplier_name?.trim();
    return prefilledName != null && prefilledName !== "" ? prefilledName : null;
  }, [
    supplierId,
    selectedSupplier?.mv_supplier_name,
    poLinesQuery.data?.supplier_name,
    invoiceQuery.data?.supplier_id,
    invoiceQuery.data?.supplier_name,
  ]);

  const poOptions = useMemo(() => {
    const fromSearch = (poQuery.data ?? []).map((po) => ({
      value: po.id,
      label: po.display_number ?? po.mv_number ?? po.id,
    }));
    const invoice = invoiceQuery.data;
    const linkedPurchaseOrderId = invoice?.purchase_order_id?.trim() ?? "";
    if (linkedPurchaseOrderId === "") {
      return fromSearch;
    }
    if (fromSearch.some((option) => option.value === linkedPurchaseOrderId)) {
      return fromSearch;
    }
    const linkedSupplierId = invoice?.supplier_id?.trim() ?? "";
    if (linkedSupplierId !== "" && linkedSupplierId !== supplierId) {
      return fromSearch;
    }
    const linkedLabel = invoice?.purchase_order_number?.trim();
    return [
      {
        value: linkedPurchaseOrderId,
        label:
          linkedLabel != null && linkedLabel !== ""
            ? linkedLabel
            : `PO #${linkedPurchaseOrderId}`,
      },
      ...fromSearch,
    ];
  }, [invoiceQuery.data, poQuery.data, supplierId]);

  const headerTotal = useMemo(() => {
    let sum = 0;
    let hasValue = false;
    for (const line of lines) {
      const quantity = parseQuantity(line.quantity);
      const unitPrice = parseUnitPrice(line.unit_price);
      if (quantity != null && unitPrice != null) {
        sum += quantity * unitPrice;
        hasValue = true;
      }
    }
    const tshTotal =
      purchaseKind === "non_po_purchase"
        ? 0
        : (parseOptionalTshAmount(tshTariffAmount) ?? 0) +
          (parseOptionalTshAmount(tshShippingAmount) ?? 0) +
          (parseOptionalTshAmount(tshOneOffAmount) ?? 0);
    if (tshTotal > 0) {
      sum += tshTotal;
      hasValue = true;
    }
    return hasValue ? sum : null;
  }, [lines, purchaseKind, tshOneOffAmount, tshShippingAmount, tshTariffAmount]);

  const invoiceTotalSubmitError = useMemo(() => {
    if (upsEnabled) return null;
    const stated = parseOptionalInvoiceTotal(statedInvoiceTotal);
    if (stated == null) return "Enter the invoice total.";
    const composed = headerTotal ?? 0;
    if (Math.round(stated * 100) === Math.round(composed * 100)) return null;
    return `Lines plus tariff, shipping, and fees add up to ${composed.toFixed(2)}. The invoice total is ${stated.toFixed(2)}.`;
  }, [headerTotal, statedInvoiceTotal, upsEnabled]);

  const splitDraftDefaults = useMemo(
    () => ({
      vendorInvoiceNumber,
      invoiceDate,
      totalCost:
        headerTotal != null ? String(headerTotal) : (invoiceQuery.data?.total_amount ?? ""),
    }),
    [headerTotal, invoiceDate, invoiceQuery.data?.total_amount, vendorInvoiceNumber],
  );

  const openSplitPanel = useCallback(
    (mode: ApInvoiceSplitModalMode) => {
      setSplitDialogError(null);
      setSplitModalMode(mode);
      setSplitModalOpen(true);
      const selected = documents.find((document) => document.id === selectedDocumentId);
      const selectedIsInvoice =
        selected != null && selected.kind !== "email" && !/\.(html?|eml)$/i.test(selected.filename);
      if (selectedIsInvoice) return;
      const invoiceDocument = documents.find(
        (document) => document.kind !== "email" && !/\.(html?|eml)$/i.test(document.filename),
      );
      if (invoiceDocument != null) setSelectedDocumentId(invoiceDocument.id);
    },
    [documents, selectedDocumentId],
  );

  const localPreviewUrl = useMemo(() => {
    if (invoiceFile == null) return null;
    return URL.createObjectURL(invoiceFile);
  }, [invoiceFile]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl != null) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  type ApInvoiceDraftSnapshot = {
    purchaseKind: ApInvoicePurchaseKind;
    lines: EditableLine[];
    purchaseOrderId: string;
    vendorInvoiceNumber: string;
    statedInvoiceTotal: string;
    supplierId: string;
    invoiceDate: string;
    paymentTerms: string;
    notes: string;
    tshTariffAmount: string;
    tshShippingAmount: string;
    tshOneOffAmount: string;
    tshFeeDescription: string;
    trackingNumbers: string[];
    trackingDraft: string;
  };

  const draftRef = useRef<ApInvoiceDraftSnapshot>({
    purchaseKind,
    lines,
    purchaseOrderId,
    vendorInvoiceNumber,
    statedInvoiceTotal,
    supplierId,
    invoiceDate,
    paymentTerms,
    notes,
    tshTariffAmount,
    tshShippingAmount,
    tshOneOffAmount,
    tshFeeDescription,
    trackingNumbers,
    trackingDraft,
  });
  draftRef.current = {
    purchaseKind,
    lines,
    purchaseOrderId,
    vendorInvoiceNumber,
    statedInvoiceTotal,
    supplierId,
    invoiceDate,
    paymentTerms,
    notes,
    tshTariffAmount,
    tshShippingAmount,
    tshOneOffAmount,
    tshFeeDescription,
    trackingNumbers,
    trackingDraft,
  };

  const payloadFromDraft = useCallback(
    (draft: ApInvoiceDraftSnapshot) => {
      const isStandaloneTsh = draft.purchaseKind === "standalone_tsh";
      const isNonPo = draft.purchaseKind === "non_po_purchase";
      const lineInputs = isStandaloneTsh
        ? []
        : draft.lines.map(isNonPo ? toExpensePatchLineInput : toPatchLineInput);
      const oneOffAmount = parseOptionalTshAmount(draft.tshOneOffAmount);
      return {
        vendor_invoice_number: draft.vendorInvoiceNumber.trim(),
        ...(upsEnabled ? {} : { total_amount: parseOptionalInvoiceTotal(draft.statedInvoiceTotal) }),
        supplier_id: draft.supplierId !== "" ? Number.parseInt(draft.supplierId, 10) : null,
        purchase_kind: draft.purchaseKind,
        standalone_tsh_source_accounts_payable_invoice_id: null,
        purchase_order_id:
          isNonPo || isStandaloneTsh || draft.purchaseOrderId === ""
            ? null
            : Number.parseInt(draft.purchaseOrderId, 10),
        invoice_date: draft.invoiceDate.trim() !== "" ? draft.invoiceDate : null,
        payment_terms: megaventoryEnumerationFromPaymentTermsUserInput(draft.paymentTerms, enumerationToLabel),
        notes: draft.notes.trim() !== "" ? draft.notes.trim() : null,
        tsh_tariff_amount: isNonPo ? null : parseOptionalTshAmount(draft.tshTariffAmount),
        tsh_shipping_amount: isNonPo ? null : parseOptionalTshAmount(draft.tshShippingAmount),
        tsh_one_off_amount: isNonPo ? null : oneOffAmount,
        tsh_fee_description:
          !isNonPo && oneOffAmount != null && draft.tshFeeDescription.trim() !== ""
            ? draft.tshFeeDescription.trim()
            : null,
        tracking_numbers: parseApInvoiceTrackingNumbers([...draft.trackingNumbers, draft.trackingDraft]),
        ...(upsEnabled ? {} : { lines: lineInputs }),
      };
    },
    [enumerationToLabel, upsEnabled],
  );

  const persistDraft = useCallback(
    (override?: Partial<ApInvoiceDraftSnapshot>): Promise<boolean> => {
      const snapshot = { ...draftRef.current, ...override };
      if (override != null) draftRef.current = snapshot;
      const next = persistChainRef.current.then(async (): Promise<boolean> => {
        if (invoiceId === "" || !hydrated) return false;
        setSaveState("saving");
        try {
          const updated = await patchApInvoice(invoiceId, payloadFromDraft(snapshot));
        queryClient.setQueryData<ApInvoiceRow>(["accounting", "ap-invoice", invoiceId], (current) => {
          if (current == null) return updated;
          return {
            ...current,
            purchase_kind: updated.purchase_kind,
            purchase_kind_label: updated.purchase_kind_label,
            purchase_order_id: updated.purchase_order_id,
          };
        });
        setSaveState("saved");
        void queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
        return true;
      } catch (error) {
        setSaveState("error");
        showToast({ tone: "error", message: formatApInvoiceError(error) });
        return false;
      }
    });
      persistChainRef.current = next.catch(() => false);
      return next;
    },
    [hydrated, invoiceId, payloadFromDraft, queryClient, showToast],
  );

  const persistDraftOnBlur = useCallback(() => {
    if (!hydrated || !canEdit || formLocked) return;
    void persistDraft();
  }, [canEdit, formLocked, hydrated, persistDraft]);

  const markFieldDirty = useCallback((fieldKey: string) => {
    setFieldSave((current) => ({ ...current, [fieldKey]: { phase: "dirty" } }));
  }, []);

  const commitField = useCallback(
    (fieldKey: string) => {
      if (!hydrated || !canEdit || formLocked) return;
      setFieldSave((current) => ({ ...current, [fieldKey]: { phase: "saving" } }));
      void persistDraft().then((saved) => {
        setFieldSave((current) => ({
          ...current,
          [fieldKey]: saved ? { phase: "saved" } : { phase: "error", error: "Could not save." },
        }));
      });
    },
    [canEdit, formLocked, hydrated, persistDraft],
  );

  const stageDraft = useCallback((override: Partial<ApInvoiceDraftSnapshot>) => {
    draftRef.current = { ...draftRef.current, ...override };
    void persistDraft();
  }, [persistDraft]);

  const retryAiReviewMutation = useMutation({
    mutationFn: () => retryApInvoiceAiReview(invoiceId),
    onSuccess: async (invoice) => {
      queryClient.setQueryData(["accounting", "ap-invoice", invoiceId], invoice);
      await queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
    },
    onError: (error) => {
      showToast({ tone: "error", message: formatApInvoiceError(error) });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => uploadApInvoiceDocument(invoiceId, file),
    onSuccess: async (invoice) => {
      setServerFilename(invoice.invoice_file_filename);
      const newest = [...(invoice.documents ?? [])].sort(
        (left, right) => Number.parseInt(right.id, 10) - Number.parseInt(left.id, 10),
      )[0];
      setSelectedDocumentId(newest?.id ?? invoice.documents?.[0]?.id ?? null);
      setInvoiceFile(null);
      void queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: ["accounting", "ap-invoice", invoiceId] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: formatApInvoiceError(error) });
    },
  });

  const handleFileChange = useCallback(
    (file: File | null) => {
      setInvoiceFile(file);
      if (file == null) return;
      uploadMutation.mutate(file);
    },
    [uploadMutation],
  );

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) => deleteApInvoiceDocument(invoiceId, documentId),
    onSuccess: async (invoice, documentId) => {
      setInvoiceFile(null);
      queryClient.setQueryData(["accounting", "ap-invoice", invoiceId], invoice);
      setSelectedDocumentId((current) =>
        current != null &&
        current !== documentId &&
        (invoice.documents ?? []).some((document) => document.id === current)
          ? current
          : (invoice.documents?.[0]?.id ?? null),
      );
      if ((invoice.documents?.length ?? 0) === 0) {
        setServerPreviewUrl(null);
        setServerFilename(null);
        setServerContentType(null);
      }
      void queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: ["accounting", "ap-invoice", invoiceId] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: formatApInvoiceError(error) });
    },
  });

  const handleRemoveDocument = useCallback(() => {
    if (selectedDocumentId != null) {
      deleteDocumentMutation.mutate(selectedDocumentId);
      return;
    }
    setInvoiceFile(null);
    setServerPreviewUrl(null);
    setServerFilename(null);
    setServerContentType(null);
  }, [deleteDocumentMutation, selectedDocumentId]);

  const mergeMutation = useMutation({
    mutationFn: (sourceInvoiceId: string) => mergeApInvoice(invoiceId, sourceInvoiceId),
    onSuccess: async (invoice) => {
      queryClient.setQueryData(["accounting", "ap-invoice", invoiceId], invoice);
      setNotes(invoice.notes ?? "");
      setLines(invoice.lines.map((line) => mapSavedLineToEditable(line, `saved-${line.id}`)));
      setSelectedDocumentId(invoice.documents?.[0]?.id ?? null);
      setMergeOpen(false);
      await queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
      await queryClient.invalidateQueries({ queryKey: ["accounting", "ap-po-vendor-invoices"] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: formatApInvoiceError(error) });
      setMergeOpen(false);
    },
  });

  const [aiIssueReportTicketId, setAiIssueReportTicketId] = useState<string | null>(null);
  const reportAiIssueMutation = useMutation({
    mutationFn: (issue: string) => reportApInvoiceAiReviewIssue(invoiceId, issue),
    onSuccess: (result) => {
      setAiIssueReportOpen(false);
      setAiIssueReportText("");
      setAiIssueReportTicketId(result.id);
      setAiIssueReportSaved(true);
    },
    onError: (error) => {
      showToast({ tone: "error", message: formatApInvoiceError(error) });
    },
  });

  const discardMutation = useMutation({
    mutationFn: () => deleteApInvoiceDraft(invoiceId),
    onSuccess: async () => {
      setDiscardConfirmOpen(false);
      await queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
      navigateToApInvoiceList();
    },
    onError: (error) => {
      showToast({ tone: "error", message: formatApInvoiceError(error) });
      setDiscardConfirmOpen(false);
    },
  });

  const returnToDraftMutation = useMutation({
    mutationFn: () => returnApInvoiceToDraft(invoiceId),
    onSuccess: async (invoice) => {
      setReturnToDraftConfirmOpen(false);
      queryClient.setQueryData(["accounting", "ap-invoice", invoiceId], invoice);
      await queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
    },
    onError: (error) => {
      showToast({ tone: "error", message: formatApInvoiceError(error) });
      setReturnToDraftConfirmOpen(false);
    },
  });

  const splitVendorMutation = useMutation({
    mutationFn: (groups: ApInvoiceSplitConfirmGroup[]) =>
      splitApInvoiceIntoVendorDrafts(
        invoiceId,
        groups.map((group) => ({
          document_ids: group.document_ids,
          vendor_invoice_number: group.vendor_invoice_number,
          invoice_date: group.invoice_date,
        })),
      ),
    onSuccess: async (result) => {
      setSplitDialogError(null);
      setSplitModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
      const nextId = result.created_invoice_ids[0];
      if (result.remaining_invoice_id == null) {
        leftDeletedInvoiceRef.current = true;
        if (nextId != null) navigate(`/accounting/ap/${nextId}/edit`);
        else navigateToApInvoiceList();
        return;
      }
      if (nextId != null && String(nextId) !== invoiceId) {
        navigate(`/accounting/ap/${nextId}/edit`);
      } else {
        void queryClient.invalidateQueries({ queryKey: ["accounting", "ap-invoice", invoiceId] });
      }
    },
    onError: (error) => {
      setSplitDialogError(formatApInvoiceError(error));
    },
    onSettled: () => {
      setSplitBusy(false);
    },
  });

  const migrateManufacturerBatchMutation = useMutation({
    mutationFn: (groups: ApInvoiceSplitConfirmGroup[]) =>
      migrateApInvoiceManufacturerBatch(invoiceId, {
        manufacturer_intent: true,
        groups: groups.map((group) => ({
          document_ids: group.document_ids,
          mfr_invoice_number: group.mfr_invoice_number,
          date: group.invoice_date,
          total_cost: Number.parseFloat(group.total_cost) || 0,
          remove_duplicate: group.remove_duplicate,
        })),
      }),
    onSuccess: async (result) => {
      setSplitDialogError(null);
      setSplitModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
      const destination = apInvoiceManufacturerMoveNavigation(result);
      if (destination.kind === "vendor-invoice") {
        await queryClient.invalidateQueries({ queryKey: ["accounting", "ap-invoice", invoiceId] });
        return;
      }
      if (destination.kind === "manufacturer-invoice") {
        leftDeletedInvoiceRef.current = true;
        navigate(`/accounting/suntronic-invoices/${destination.manufacturerInvoiceId}`);
        return;
      }
      leftDeletedInvoiceRef.current = true;
      navigateToApInvoiceList();
    },
    onError: (error) => {
      setSplitDialogError(formatApInvoiceError(error));
    },
    onSettled: () => {
      setSplitBusy(false);
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (upsQuery.data?.enabled === true) {
        const submitMultiPoTsh = multiPoTshSubmitRef.current;
        if (submitMultiPoTsh == null) {
          throw new Error("Multi-PO TSH lines are not ready to submit.");
        }
        return submitMultiPoTsh();
      }
      const saved = await persistDraft();
      if (!saved) {
        throw new Error("Could not save draft changes before submit.");
      }
      return submitApInvoice(invoiceId);
    },
    onSuccess: async (invoice) => {
      queryClient.setQueryData(["accounting", "ap-invoice", invoiceId], invoice);
      await queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
      if (invoice.status === "submitted") {
        navigateToApInvoiceList();
      }
    },
    onError: (error) => {
      showToast({ tone: "error", message: formatApInvoiceError(error) });
    },
  });

  const cbpTshQuery = useQuery({
    queryKey: ["accounting", "ap-invoice-cbp-tsh", invoiceId],
    queryFn: () => fetchApInvoiceCbpTsh(invoiceId),
    enabled: invoiceId !== "" && purchaseKind === "standalone_tsh",
  });
  const cbpPeriodicMonthlyStatement = cbpTshQuery.data?.role === "cbp";
  const supplierPaymentTermsBaseline = paymentTermsMvFromSupplier(
    selectedSupplier?.payment_terms ?? invoiceQuery.data?.supplier_payment_terms,
  );
  const paymentTermsCurrent = megaventoryEnumerationFromPaymentTermsUserInput(paymentTerms, enumerationToLabel);
  const paymentTermsDiffersFromSupplier =
    !cbpPeriodicMonthlyStatement &&
    paymentTermsCurrent.trim() !== "" &&
    supplierPaymentTermsBaseline.trim() !== "" &&
    paymentTermsCurrent !==
      megaventoryEnumerationFromPaymentTermsUserInput(supplierPaymentTermsBaseline, enumerationToLabel);
  const paymentTermsSelectRows = (() => {
    const pairs = Object.entries(enumerationToLabel).map(([mvEnumeration, displayLabel]) => ({
      mvEnumeration,
      displayLabel,
    }));
    pairs.sort((first, second) =>
      first.displayLabel.localeCompare(second.displayLabel, undefined, { sensitivity: "base" }),
    );
    if (
      paymentTermsCurrent !== "" &&
      !Object.prototype.hasOwnProperty.call(enumerationToLabel, paymentTermsCurrent)
    ) {
      pairs.unshift({
        mvEnumeration: paymentTermsCurrent,
        displayLabel:
          mapMegaventoryPaymentTermsToLabel(paymentTermsCurrent, enumerationToLabel) ?? paymentTermsCurrent,
      });
    }
    return pairs;
  })();

  if (invoiceQuery.isLoading || !hydrated) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-slate-600">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {pendingTshWorkflow
          ? "Loading TSH workflow…"
          : submittedReview
            ? "Loading vendor invoice…"
            : "Loading draft invoice…"}
      </div>
    );
  }

  if (invoiceQuery.isError && !splitBusy) {
    const invoiceErrorMessage =
      invoiceQuery.error instanceof Error ? invoiceQuery.error.message : String(invoiceQuery.error);
    return (
      <p className="text-sm text-rose-700">{invoiceErrorMessage}</p>
    );
  }

  const previewUrl = localPreviewUrl ?? serverPreviewUrl;
  const hasStoredDocuments = previewUrl != null || serverFilename != null || documents.length > 0;
  const showAiReviewButton = workflowStatus === "draft" && hasStoredDocuments;
  const showRetryAiReviewButton = canRetryApInvoiceAiReview(workflowStatus, hasStoredDocuments);
  const aiReviewInProgress =
    workflowStatus === "under_ai_review" || retryAiReviewMutation.isPending;
  const canDiscardDraft = isApInvoiceDeletable(workflowStatus);
  const footerBusy =
    submitMutation.isPending ||
    uploadMutation.isPending ||
    deleteDocumentMutation.isPending ||
    retryAiReviewMutation.isPending ||
    discardMutation.isPending ||
    returnToDraftMutation.isPending ||
    splitVendorMutation.isPending ||
    migrateManufacturerBatchMutation.isPending ||
    aiReviewInProgress ||
    saveState === "saving";
  const showSplitVendorButton = canDiscardDraft && documents.length > 1;
  const savedPurchaseOrderId = invoiceQuery.data?.purchase_order_id ?? "";
  const showMergeButton =
    purchaseKind === "po_inventory" &&
    purchaseOrderId !== "" &&
    savedPurchaseOrderId === purchaseOrderId &&
    (invoiceQuery.data?.purchase_invoice_id == null || invoiceQuery.data.purchase_invoice_id === "") &&
    invoiceQuery.data?.merged_into_accounts_payable_invoice_id == null &&
    invoiceQuery.data?.gipi_sync_status !== "creating" &&
    invoiceQuery.data?.gipi_sync_status !== "created";
  const showManufacturerMigrateButton = canDiscardDraft;
  const tshWorkflowActionable =
    invoiceQuery.data != null && canCompleteApInvoiceTshWorkflow(invoiceQuery.data);
  const showTshWorkflowPanel =
    invoiceQuery.data != null &&
    (pendingTshWorkflow || (submittedReview && apInvoiceShowsTshWorkflow(invoiceQuery.data)));
  const pageTitle = submittedReview
    ? "View vendor invoice"
    : pendingTshWorkflow
      ? tshWorkflowActionable
        ? "Complete TSH workflow"
        : "Vendor invoice"
      : workflowStatus === "awaiting_human_review"
        ? "Review vendor invoice"
        : "Add vendor invoice";
  const isStandaloneTshKind = purchaseKind === "standalone_tsh";
  const isNonPoKind = purchaseKind === "non_po_purchase";
  const invoiceHeaderSaveStatus =
    aiReviewInProgress ? (
      <span className="inline-flex items-center gap-1 text-xs text-violet-700">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        AI review in progress…
      </span>
    ) : saveState === "saving" ? (
      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Saving…
      </span>
    ) : saveState === "saved" ? (
      <span className="text-xs text-slate-500">All changes saved</span>
    ) : null;

  const showReportAiIssueButton =
    invoiceQuery.data?.ai_review_error != null ||
    invoiceQuery.data?.ai_review_requested_at != null ||
    invoiceQuery.data?.ai_review_completed_at != null;
  const reviewingInvoice = pendingTshWorkflow || submittedReview;
  const canReturnToDraft =
    invoiceQuery.data != null && canReturnApInvoiceToDraftBeforeGipi(invoiceQuery.data);
  const showMegaventoryForward = pendingTshWorkflow && invoiceQuery.data != null;
  const submitBlocked =
    submitMutation.isPending ||
    uploadMutation.isPending ||
    retryAiReviewMutation.isPending ||
    discardMutation.isPending ||
    formLocked ||
    saveState === "saving" ||
    (!isNonPoKind && lines.some((line) => apInvoiceSkuTranslationIsAlert(line.sku_match_method))) ||
    invoiceTotalSubmitError != null;

  const overflowItems: ApInvoiceHeaderMenuItem[] = [];
  if (!earlyPayment.hidden) {
    overflowItems.push({
      id: "early-payment",
      label: earlyPayment.label,
      disabled: earlyPayment.disabled,
      onSelect: earlyPayment.onSelect,
    });
  }
  if (!reviewingInvoice && showRetryAiReviewButton) {
    overflowItems.push({
      id: "ai-review",
      label: retryAiReviewMutation.isPending
        ? "Starting AI review…"
        : aiReviewInProgress
          ? "AI review running…"
          : workflowStatus === "draft"
            ? "Run AI review"
            : "Retry AI review",
      disabled: footerBusy,
      onSelect: () => retryAiReviewMutation.mutate(),
    });
  }
  if (!reviewingInvoice && showMergeButton) {
    overflowItems.push({
      id: "merge",
      label: "Merge vendor invoice",
      disabled: mergeMutation.isPending,
      onSelect: () => setMergeOpen(true),
    });
  }
  if (!reviewingInvoice && showSplitVendorButton) {
    overflowItems.push({
      id: "split-vendor",
      label: "Split into vendor invoices",
      disabled: footerBusy,
      onSelect: () => openSplitPanel("vendor"),
    });
  }
  if (!reviewingInvoice && showManufacturerMigrateButton) {
    overflowItems.push({
      id: "move-manufacturer",
      label: migrateManufacturerBatchMutation.isPending ? "Moving…" : "Move to manufacturer invoices",
      disabled: footerBusy,
      onSelect: () => openSplitPanel("manufacturer"),
    });
  }
  if (reviewingInvoice && canReturnToDraft && showMegaventoryForward) {
    overflowItems.push({
      id: "return-to-draft",
      label: returnToDraftMutation.isPending ? "Returning…" : "Return to draft",
      disabled: returnToDraftMutation.isPending,
      onSelect: () => setReturnToDraftConfirmOpen(true),
    });
  }
  if (showReportAiIssueButton) {
    overflowItems.push({
      id: "report-ai",
      label: "Report issues with AI run",
      disabled: reportAiIssueMutation.isPending,
      onSelect: () => {
        setAiIssueReportSaved(false);
        setAiIssueReportOpen(true);
      },
    });
  }

  const invoiceBackAction = reviewingInvoice ? (
    <Button type="button" variant="secondary" onClick={navigateToApInvoiceList}>
      Back to vendor invoices
    </Button>
  ) : (
    <Button
      type="button"
      variant="danger"
      disabled={!canDiscardDraft || footerBusy}
      onClick={() => setDiscardConfirmOpen(true)}
    >
      {discardMutation.isPending ? "Discarding…" : "Discard draft"}
    </Button>
  );

  const invoiceForwardAction = reviewingInvoice ? (
    showMegaventoryForward && invoiceQuery.data != null ? (
      <ApInvoiceTshOpenMegaventoryButton
        invoiceId={invoiceId}
        invoice={invoiceQuery.data}
        onUpdated={handleInvoiceUpdated}
      />
    ) : canReturnToDraft ? (
      <Button
        type="button"
        variant="secondary"
        disabled={returnToDraftMutation.isPending}
        onClick={() => setReturnToDraftConfirmOpen(true)}
      >
        {returnToDraftMutation.isPending ? "Returning…" : "Return to draft"}
      </Button>
    ) : null
  ) : (
    <Button type="button" disabled={submitBlocked} onClick={() => submitMutation.mutate()}>
      {submitMutation.isPending ? "Submitting…" : "Submit invoice"}
    </Button>
  );

  const invoiceFormSection = (
    <div className="flex flex-col gap-4">
      {invoiceQuery.data?.merged_into_accounts_payable_invoice_id != null ? (
        <p className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700">
          This invoice was merged into{" "}
          <Link
            className="font-medium text-sky-700 underline"
            to={`/accounting/ap/${invoiceQuery.data.merged_into_accounts_payable_invoice_id}/edit`}
          >
            vendor invoice {invoiceQuery.data.merged_into_accounts_payable_invoice_id}
          </Link>
          . It will not create its own purchase invoice.
        </p>
      ) : null}
      <Card className="shrink-0">
        <CardHeader>
          <CardTitle>Invoice details</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="min-w-0">
            <ApInvoiceDetailFieldLabel label="Classification" hoverHint={PURCHASE_KIND_HELP} />
            <SegmentedControl<"po_inventory" | "non_po_purchase" | "standalone_tsh" | "multi_po_tsh">
              fill
              className="w-full"
              value={upsEnabled ? "multi_po_tsh" : purchaseKind}
              disabled={formLocked || upsModeMutation.isPending}
              aria-label="Classification"
              onChange={(next) => {
                const nextKind = next === "multi_po_tsh" ? "non_po_purchase" : next;
                const nextLines = nextKind === "standalone_tsh" ? [] : lines;
                const nextPurchaseOrderId =
                  nextKind === "non_po_purchase" || nextKind === "standalone_tsh" ? "" : purchaseOrderId;
                if (next === "multi_po_tsh") {
                  upsModeMutation.mutate(true);
                } else if (upsEnabled) {
                  upsModeMutation.mutate(false);
                }
                setPurchaseKind(nextKind);
                setPurchaseOrderId(nextPurchaseOrderId);
                if (nextKind !== "po_inventory") {
                  setPoCurrency(null);
                }
                if (nextKind === "non_po_purchase") {
                  setTshTariffAmount("");
                  setTshShippingAmount("");
                  setTshOneOffAmount("");
                  setTshFeeDescription("");
                }
                if (nextKind === "standalone_tsh") {
                  setLines([]);
                }
                queryClient.setQueryData<ApInvoiceRow>(["accounting", "ap-invoice", invoiceId], (current) => {
                  if (current == null) return current;
                  return {
                    ...current,
                    purchase_kind: nextKind,
                    purchase_kind_label: next === "multi_po_tsh" ? "Multi-PO TSH" : current.purchase_kind_label,
                    purchase_order_id: nextPurchaseOrderId === "" ? null : nextPurchaseOrderId,
                  };
                });
                if (next === "multi_po_tsh") return;
                void persistDraft({
                  purchaseKind: nextKind,
                  lines: nextLines,
                  purchaseOrderId: nextPurchaseOrderId,
                  ...(nextKind === "non_po_purchase"
                    ? {
                        tshTariffAmount: "",
                        tshShippingAmount: "",
                        tshOneOffAmount: "",
                        tshFeeDescription: "",
                      }
                    : {}),
                });
              }}
              options={[
                { value: "po_inventory", label: apInvoicePurchaseKindLabel("po_inventory") },
                { value: "non_po_purchase", label: apInvoicePurchaseKindLabel("non_po_purchase") },
                { value: "standalone_tsh", label: apInvoicePurchaseKindLabel("standalone_tsh") },
                { value: "multi_po_tsh", label: "Multi-PO TSH" },
              ]}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="min-w-0">
              <ApInvoiceDetailFieldLabel label="Vendor" />
              <div className="flex flex-wrap items-center gap-2">
                <SupplierSearchableSelect
                  className="min-w-0 flex-1"
                  value={supplierId}
                  extraRows={supplierExtraRows}
                  onValueChange={(option) => {
                    const nextSupplierId = option?.value ?? "";
                    setSupplierId(nextSupplierId);
                    const selectedPoSupplierId =
                      poQuery.data?.find((row) => row.id === purchaseOrderId)?.supplier_id
                      ?? poLinesQuery.data?.supplier_id
                      ?? null;
                    const clearPo =
                      (purchaseOrderId !== ""
                        && selectedPoSupplierId != null
                        && String(selectedPoSupplierId) !== nextSupplierId)
                      || nextSupplierId === "";
                    const nextLines = clearPo
                      ? lines.map((line) => ({ ...line, purchase_order_item_id: null }))
                      : lines;
                    if (clearPo) {
                      setPurchaseOrderId("");
                      setPoCurrency(null);
                      setLines(nextLines);
                    } else if (purchaseOrderId === "") {
                      setPoCurrency(null);
                    }
                    stageDraft({
                      supplierId: nextSupplierId,
                      purchaseOrderId: clearPo ? "" : purchaseOrderId,
                      lines: nextLines,
                    });
                  }}
                  searchValue={supplierSearch}
                  onSearchChange={setSupplierSearch}
                  placeholder=""
                  searchPlaceholder="Search vendors…"
                  emptyMessage="No vendors match."
                  selectedLabelOverride={supplierLabelOverride}
                  disabled={formLocked}
                />
                {supplierId !== "" ? (
                  <span className="text-sm text-slate-500">{supplierCurrency}</span>
                ) : null}
              </div>
            </div>
            <div className="min-w-0">
              <ApInvoiceDetailFieldLabel
                htmlFor="ap-add-invoice-date"
                label={cbpPeriodicMonthlyStatement ? "Entry date" : "Invoice date"}
                hoverHint={
                  cbpPeriodicMonthlyStatement
                    ? "CBP entry date from the entry summary. The monthly debit is the 15th business day of the following month."
                    : undefined
                }
              />
              <Input
                id="ap-add-invoice-date"
                type="date"
                value={invoiceDate}
                onChange={(event) => setInvoiceDate(event.target.value)}
                onBlur={persistDraftOnBlur}
                disabled={formLocked}
              />
            </div>
            {upsEnabled ? null : (
            <div className="min-w-0">
              <ApInvoiceDetailFieldLabel
                htmlFor="ap-add-invoice-total"
                label="Invoice total"
                hoverHint="The total printed on the vendor invoice. Enter this before tariff, shipping, and fees. Submit stays blocked until the lines plus those charges add up to it."
              />
              <Input
                id="ap-add-invoice-total"
                type="number"
                min="0"
                step="0.01"
                value={statedInvoiceTotal}
                onChange={(event) => {
                  setStatedInvoiceTotal(event.target.value);
                  submitMutation.reset();
                }}
                onBlur={persistDraftOnBlur}
                disabled={formLocked}
              />
              {invoiceTotalSubmitError != null ? (
                <p className="mt-1 text-sm text-rose-700" role="alert">
                  {invoiceTotalSubmitError}
                </p>
              ) : null}
            </div>
            )}
            <div className="min-w-0">
              <ApInvoiceDetailFieldLabel htmlFor="ap-add-vendor-number" label="Vendor invoice number" />
              <Input
                id="ap-add-vendor-number"
                value={vendorInvoiceNumber}
                onChange={(event) => {
                  setVendorInvoiceNumber(event.target.value);
                  submitMutation.reset();
                }}
                onBlur={persistDraftOnBlur}
                disabled={formLocked}
              />
            </div>
            <div className="min-w-0">
              <ApInvoiceDetailFieldLabel
                htmlFor="ap-add-payment-terms"
                label="Payment terms"
                hoverHint={PAYMENT_TERMS_HELP}
              />
              <select
                id="ap-add-payment-terms"
                value={paymentTermsCurrent}
                disabled={formLocked}
                onChange={(event) => {
                  paymentTermsUserEditedRef.current = true;
                  setPaymentTerms(
                    megaventoryEnumerationFromPaymentTermsUserInput(event.target.value, enumerationToLabel),
                  );
                }}
                onBlur={persistDraftOnBlur}
                className={cn(
                  "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500",
                  paymentTermsDiffersFromSupplier &&
                    "border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500",
                )}
              >
                {paymentTermsSelectRows.map((row) => (
                  <option key={row.mvEnumeration} value={row.mvEnumeration}>
                    {row.displayLabel}
                  </option>
                ))}
              </select>
              {cbpPeriodicMonthlyStatement ? (
                <p className="mt-1 text-xs font-medium text-amber-800">
                  Periodic Monthly Statement. Customs debits on the 15th business day of the month after the entry date
                  {cbpTshQuery.data?.pms_due_on != null ? `, due ${cbpTshQuery.data.pms_due_on}` : ""}. This is not the
                  vendor Net 30 default.
                </p>
              ) : null}
              {paymentTermsDiffersFromSupplier ? (
                <p className="mt-1 text-xs font-medium text-amber-800">
                  Differs from vendor default (
                  {mapMegaventoryPaymentTermsToLabel(supplierPaymentTermsBaseline, enumerationToLabel) ??
                    supplierPaymentTermsBaseline}
                  )
                </p>
              ) : null}
            </div>
            {isStandaloneTshKind ? (
              <div className="min-w-0 md:col-span-2">
                <ApInvoiceDetailFieldLabel label="GIPI document" hoverHint={STANDALONE_TSH_GIPI_HELP} />
                {invoiceQuery.data != null ? (
                  <ApInvoiceTshParentSelector
                    invoiceId={invoiceId}
                    invoice={invoiceQuery.data}
                    standaloneDraft
                    disabled={formLocked}
                    onUpdated={handleInvoiceUpdated}
                  />
                ) : null}
              </div>
            ) : null}
            {purchaseKind === "po_inventory" ? (
            <div className="min-w-0">
              <ApInvoiceDetailFieldLabel label="Purchase order" hoverHint={PO_LINK_HELP} />
              <SearchableSelect
                className="w-full"
                value={purchaseOrderId}
                onValueChange={(option) => {
                  const nextPoId = option?.value ?? "";
                  poPrefillTokenRef.current = null;
                  setPurchaseOrderId(nextPoId);
                  if (nextPoId === "") {
                    setLines([]);
                    setPoCurrency(null);
                    stageDraft({ purchaseOrderId: "", lines: [] });
                    return;
                  }
                  const selectedPo = poQuery.data?.find((row) => row.id === nextPoId);
                  const nextSupplierId =
                    selectedPo?.supplier_id != null && selectedPo.supplier_id.trim() !== ""
                      ? selectedPo.supplier_id
                      : supplierId;
                  if (nextSupplierId !== supplierId) {
                    setSupplierId(nextSupplierId);
                  }
                  stageDraft({ purchaseOrderId: nextPoId, supplierId: nextSupplierId });
                }}
                options={poOptions}
                searchValue={poSearch}
                onSearchChange={setPoSearch}
                placeholder={supplierId === "" ? "Select a vendor first" : "Optional PO link…"}
                searchPlaceholder="Search PO number…"
                emptyMessage={supplierId === "" ? "Select a vendor first." : "No purchase orders match."}
                isLoading={poQuery.isLoading}
                disabled={formLocked || supplierId === ""}
              />
              {purchaseOrderId !== "" && poLinesQuery.isLoading ? (
                <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  Loading purchase order lines…
                </p>
              ) : null}
            </div>
            ) : null}
            {purchaseKind === "po_inventory" && purchaseOrderId !== "" ? (
              <div className="min-w-0">
                <ApInvoicePoSiblingInvoices
                  invoiceId={invoiceId}
                  purchaseOrderId={purchaseOrderId}
                  canMerge={showMergeButton}
                  mergePending={mergeMutation.isPending}
                  onMerge={(sourceInvoiceId) => mergeMutation.mutate(sourceInvoiceId)}
                />
              </div>
            ) : null}
            {upsEnabled ? null : (
            <div className="min-w-0 md:col-span-2">
              <ApInvoiceDetailFieldLabel
                htmlFor="ap-add-tracking"
                label="Tracking"
                hoverHint={AP_INVOICE_TRACKING_HOVER_HINT}
              />
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">From this invoice</p>
              <div className="mb-3 flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5">
                {trackingNumbers.map((tracking) => {
                  const chip = resolveApInvoiceTrackingChipPresentation({
                    trackingNumber: tracking,
                    selected: trackingNumbers,
                    extractedFromInvoice: extractedTracking,
                    purchaseOrderCandidates: purchaseOrderTracking,
                  });
                  const sourceLabel = apInvoiceTrackingChipSourceLabel(chip);
                  return (
                    <span
                      key={tracking}
                      className={
                        chip.crossConfirmed
                          ? "inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-900 ring-1 ring-emerald-200"
                          : "inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-1 text-xs font-medium text-brand-900 ring-1 ring-brand-200"
                      }
                    >
                      {chip.displayNumber}
                      <span className="font-normal text-slate-500">{sourceLabel}</span>
                      {formLocked ? null : (
                        <button
                          type="button"
                          className="rounded p-0.5 text-slate-500 hover:bg-white/60 hover:text-slate-800"
                          aria-label={`Remove tracking ${tracking}`}
                          onClick={() => {
                            const next = toggleApInvoiceTrackingNumber(trackingNumbers, tracking);
                            setTrackingNumbers(next);
                            stageDraft({ trackingNumbers: next });
                          }}
                        >
                          <X className="h-3 w-3" aria-hidden />
                        </button>
                      )}
                    </span>
                  );
                })}
                {formLocked ? null : (
                  <Input
                    id="ap-add-tracking"
                    value={trackingDraft}
                    onChange={(event) => setTrackingDraft(event.target.value)}
                    onBlur={() => {
                      const parsed = parseApInvoiceTrackingNumbers(trackingDraft);
                      if (parsed.length === 0) {
                        persistDraftOnBlur();
                        return;
                      }
                      const next = parseApInvoiceTrackingNumbers([...trackingNumbers, ...parsed]);
                      setTrackingNumbers(next);
                      setTrackingDraft("");
                      stageDraft({ trackingNumbers: next, trackingDraft: "" });
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      const parsed = parseApInvoiceTrackingNumbers(trackingDraft);
                      if (parsed.length === 0) return;
                      const next = parseApInvoiceTrackingNumbers([...trackingNumbers, ...parsed]);
                      setTrackingNumbers(next);
                      setTrackingDraft("");
                      stageDraft({ trackingNumbers: next, trackingDraft: "" });
                    }}
                    aria-label="Tracking number"
                    disabled={formLocked}
                    className="h-8 min-w-[10rem] flex-1 border-0 bg-transparent px-1 shadow-none focus-visible:ring-0"
                  />
                )}
              </div>
              {purchaseOrderId !== "" && purchaseOrderTrackingAvailable.length > 0 ? (
                <>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                    From purchase order
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {purchaseOrderTrackingAvailable.map((candidate) => (
                      <button
                        key={`${candidate.source}-${candidate.tracking_number}`}
                        type="button"
                        disabled={formLocked}
                        onClick={() => {
                          const next = toggleApInvoiceTrackingNumber(trackingNumbers, candidate.tracking_number);
                          setTrackingNumbers(next);
                          stageDraft({ trackingNumbers: next });
                        }}
                        className="rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
                      >
                        {candidate.tracking_number}
                        <span className="ml-1 font-normal text-slate-500">{candidate.source_label}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
              {purchaseOrderId !== "" && purchaseOrderTracking.length > 1 ? (
                <p className="mt-2 text-xs text-slate-500">
                  This purchase order has more than one tracking number. Pick the one that belongs on this invoice.
                </p>
              ) : null}
            </div>
            )}
          </div>
          {upsEnabled || isNonPoKind ? null : (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="min-w-0">
              <ApInvoiceDetailFieldLabel htmlFor="ap-add-tsh-tariff" label="Tariff" hoverHint={TSH_TARIFF_HOVER_HINT} />
              <Input
                id="ap-add-tsh-tariff"
                type="number"
                min="0"
                step="0.01"
                value={tshTariffAmount}
                onChange={(event) => setTshTariffAmount(event.target.value)}
                onBlur={persistDraftOnBlur}
                placeholder="0.00"
                disabled={formLocked}
              />
            </div>
            <div className="min-w-0">
              <ApInvoiceDetailFieldLabel htmlFor="ap-add-tsh-shipping" label="Shipping" hoverHint={TSH_SHIPPING_HOVER_HINT} />
              <Input
                id="ap-add-tsh-shipping"
                type="number"
                min="0"
                step="0.01"
                value={tshShippingAmount}
                onChange={(event) => setTshShippingAmount(event.target.value)}
                onBlur={persistDraftOnBlur}
                placeholder="0.00"
                disabled={formLocked}
              />
            </div>
            <div className="min-w-0">
              <ApInvoiceDetailFieldLabel htmlFor="ap-add-tsh-one-off" label="One-off fee" hoverHint={TSH_ONE_OFF_HOVER_HINT} />
              <Input
                id="ap-add-tsh-one-off"
                type="number"
                min="0"
                step="0.01"
                value={tshOneOffAmount}
                onChange={(event) => setTshOneOffAmount(event.target.value)}
                onBlur={persistDraftOnBlur}
                placeholder="0.00"
                disabled={formLocked}
              />
            </div>
            {showOneOffFeeDescription ? (
              <div className="min-w-0">
                <ApInvoiceDetailFieldLabel
                  htmlFor="ap-add-tsh-fee-description"
                  label="One-off fee description"
                  hoverHint={TSH_ONE_OFF_DESCRIPTION_HOVER_HINT}
                />
                <Input
                  id="ap-add-tsh-fee-description"
                  value={tshFeeDescription}
                  onChange={(event) => setTshFeeDescription(event.target.value)}
                  onBlur={persistDraftOnBlur}
                  placeholder="For example restocking fee"
                  disabled={formLocked}
                />
              </div>
            ) : null}
          </div>
          )}
          {showStandaloneParentSelector && invoiceQuery.data != null ? (
            <ApInvoiceTshParentSelector
              invoiceId={invoiceId}
              invoice={invoiceQuery.data}
              standaloneDraft
              onUpdated={handleInvoiceUpdated}
            />
          ) : null}
          {isStandaloneTshKind ? <ApInvoiceCbpTshPanel invoiceId={invoiceId} /> : null}
          <ApInvoiceCommentsPanel invoiceId={invoiceId} />
        </CardBody>
      </Card>

      {upsEnabled ? (
        <ApInvoiceUpsPanel
          invoiceId={invoiceId}
          formLocked={formLocked}
          onInvoiceUpdated={handleInvoiceUpdated}
          submitRef={multiPoTshSubmitRef}
        />
      ) : (
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle>Line items</CardTitle>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setLines((current) => [...current, newBlankLine()])}
            disabled={formLocked}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add line
          </Button>
        </CardHeader>
        <CardBody className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          {lines.length === 0 ? (
            purchaseOrderId !== "" && poLinesQuery.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading purchase order lines…
              </p>
            ) : purchaseOrderId !== "" && poLinesQuery.isError ? (
              <p className="text-sm text-rose-700">
                {poLinesQuery.error instanceof Error
                  ? poLinesQuery.error.message
                  : "Could not load purchase order lines."}
              </p>
            ) : isNonPoKind ? (
              <p className="text-sm text-slate-500">
                Add a line for each expense. Each line needs a description, an amount, and a category.
              </p>
            ) : (
              <p className="text-sm text-slate-500">
                Link a purchase order to prefill lines, or add lines manually to match the vendor invoice.
              </p>
            )
          ) : (
            <div className="space-y-3">
              {!isNonPoKind && lines.some((line) => apInvoiceSkuTranslationIsAlert(line.sku_match_method)) ? (
                <p
                  role="alert"
                  className="flex items-start gap-2 rounded-md bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-900 ring-1 ring-rose-200"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  One or more lines have a manufacturer part that is not approved for that stock keeping unit on the AML.
                </p>
              ) : null}
              <ul className="space-y-3">
                {lines.map((line) => {
                  if (isNonPoKind) {
                    return (
                      <li
                        key={line.clientKey}
                        className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                      >
                        <div className="mb-2 flex justify-end">
                          <button
                            type="button"
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-700"
                            aria-label="Remove line"
                            onClick={() => {
                              const next = lines.filter((row) => row.clientKey !== line.clientKey);
                              setLines(next);
                              stageDraft({ lines: next });
                            }}
                            disabled={formLocked}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        </div>
                        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,9rem)_minmax(0,16rem)]">
                          <div className="min-w-0">
                            <ApInvoiceDetailFieldLabel label="Description" />
                            <textarea
                              value={line.description}
                              onChange={(event) =>
                                setLines((current) =>
                                  current.map((row) =>
                                    row.clientKey === line.clientKey
                                      ? { ...row, description: event.target.value }
                                      : row,
                                  ),
                                )
                              }
                              onBlur={persistDraftOnBlur}
                              placeholder="What this charge is for"
                              aria-label="Line description"
                              disabled={formLocked}
                              className={LINE_DESCRIPTION_CLASS}
                            />
                          </div>
                          <div className="min-w-0">
                            <ApInvoiceDetailFieldLabel label="Amount" />
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.unit_price}
                              onChange={(event) =>
                                setLines((current) =>
                                  current.map((row) =>
                                    row.clientKey === line.clientKey
                                      ? { ...row, unit_price: event.target.value, quantity: "1" }
                                      : row,
                                  ),
                                )
                              }
                              onBlur={persistDraftOnBlur}
                              placeholder="0.00"
                              aria-label="Line amount"
                              disabled={formLocked}
                            />
                          </div>
                          <ApInvoiceExpenseCategoryField
                            lineKey={line.clientKey}
                            accountId={line.qbo_expense_account_id}
                            accountName={line.qbo_expense_account_name}
                            disabled={formLocked}
                            onChange={(account) => {
                              const next = lines.map((row) =>
                                row.clientKey === line.clientKey
                                  ? {
                                      ...row,
                                      qbo_expense_account_id: account?.id ?? null,
                                      qbo_expense_account_name: account?.name ?? null,
                                    }
                                  : row,
                              );
                              setLines(next);
                              stageDraft({ lines: next });
                            }}
                          />
                        </div>
                      </li>
                    );
                  }
                  const ourSku = apInvoiceLineOurSku(line);
                  const mpn = apInvoiceLineMpn(line);
                  const vendorPart = apInvoiceLineVendorPart(line);
                  const finishedGoodSku = apInvoiceLineFinishedGoodSku(line);
                  const showFinishedGoodField = apInvoiceLineShowsFinishedGoodField(line);
                  const skuTranslation = apInvoiceTestedSkuTranslation(line);
                  const skuAlert = apInvoiceSkuTranslationIsAlert(line.sku_match_method);
                  return (
                    <li
                      key={line.clientKey}
                      className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                    >
                      <div className="grid gap-3 md:grid-cols-[minmax(16rem,18rem)_minmax(0,1fr)_minmax(0,6.5rem)] md:items-stretch">
                        <div className="flex min-w-0 flex-col gap-2">
                          <div className="min-w-0">
                            <ApInvoiceDetailFieldLabel
                              label={showFinishedGoodField ? "Labor SKU" : "SKU"}
                              hoverHint={LINE_OUR_SKU_HELP}
                            />
                            <AssemblySkuCombobox
                              value={ourSku}
                              onChange={(next) =>
                                setLines((current) =>
                                  current.map((row) => {
                                    if (row.clientKey !== line.clientKey) return row;
                                    const nextTrimmed = next.trim();
                                    const switchingToLabor = isManufacturingLaborSku(nextTrimmed);
                                    const currentMpn = (row.extracted_mpn ?? "").trim();
                                    const matchedPoLine =
                                      switchingToLabor && poLinesQuery.data != null
                                        ? poLinesQuery.data.lines.find(
                                            (poLine) =>
                                              poLine.sku.trim().toLowerCase() === nextTrimmed.toLowerCase(),
                                          )
                                        : null;
                                    const promotedFinishedGood =
                                      switchingToLabor && (row.finished_good_sku ?? "").trim() === ""
                                        ? ((matchedPoLine?.finished_good_sku ?? "").trim()
                                          || parseFinishedGoodSkuFromLaborDescription(row.description)
                                          || (currentMpn !== "" && currentMpn.toLowerCase() !== nextTrimmed.toLowerCase()
                                            ? currentMpn
                                            : null))
                                        : row.finished_good_sku;
                                    return {
                                      ...row,
                                      extracted_our_part_number: next,
                                      sku: nextTrimmed !== "" ? nextTrimmed : row.sku,
                                      purchase_order_item_id:
                                        matchedPoLine != null
                                          ? Number.parseInt(matchedPoLine.id, 10)
                                          : row.purchase_order_item_id,
                                      finished_good_sku: promotedFinishedGood ?? null,
                                      extracted_mpn: switchingToLabor ? null : row.extracted_mpn,
                                      extracted_part_number: switchingToLabor
                                        ? row.extracted_vendor_part_number
                                        : row.extracted_part_number,
                                    };
                                  }),
                                )
                              }
                              onBlurCommittedWithSku={() => {
                                testManufacturerParts();
                                persistDraftOnBlur();
                              }}
                              enforceCatalogSku
                              linkOpenDiscrepancy={false}
                              placeholder={showFinishedGoodField ? "Search labor SKU" : "Search SKU"}
                              disabled={formLocked}
                              className="w-full"
                            />
                          </div>
                          {showFinishedGoodField ? (
                            <div className="min-w-0">
                              <ApInvoiceDetailFieldLabel
                                label="Finished good SKU"
                                hoverHint={LINE_FINISHED_GOOD_SKU_HELP}
                              />
                              <AssemblySkuCombobox
                                value={finishedGoodSku}
                                onChange={(next) =>
                                  setLines((current) =>
                                    current.map((row) =>
                                      row.clientKey === line.clientKey
                                        ? {
                                            ...row,
                                            finished_good_sku: next.trim() !== "" ? next.trim() : null,
                                          }
                                        : row,
                                    ),
                                  )
                                }
                                onBlurCommittedWithSku={() => {
                                  persistDraftOnBlur();
                                }}
                                enforceCatalogSku
                                placeholder="Search finished good SKU"
                                disabled={formLocked}
                                className="w-full"
                              />
                            </div>
                          ) : (
                            <ApInvoiceFieldSaveFrame
                              fieldKey={`${line.clientKey}:mpn`}
                              label="MPN"
                              hoverHint={LINE_MPN_HELP}
                              phase={fieldSave[`${line.clientKey}:mpn`]?.phase}
                              error={fieldSave[`${line.clientKey}:mpn`]?.error}
                            >
                              <Input
                                value={mpn}
                                onChange={(event) => {
                                  markFieldDirty(`${line.clientKey}:mpn`);
                                  setLines((current) =>
                                    current.map((row) =>
                                      row.clientKey === line.clientKey
                                        ? {
                                            ...row,
                                            extracted_mpn: event.target.value,
                                            extracted_part_number: event.target.value,
                                          }
                                        : row,
                                    ),
                                  );
                                }}
                                placeholder="Manufacturer part number"
                                aria-label="Manufacturer part number"
                                disabled={formLocked}
                                className={skuAlert ? "w-full ring-2 ring-rose-400" : "w-full"}
                                onBlur={() => {
                                  testManufacturerParts();
                                  commitField(`${line.clientKey}:mpn`);
                                }}
                              />
                            </ApInvoiceFieldSaveFrame>
                          )}
                          <ApInvoiceFieldSaveFrame
                            fieldKey={`${line.clientKey}:vendor-part`}
                            label="Vendor part"
                            hoverHint={LINE_VENDOR_PART_HELP}
                            phase={fieldSave[`${line.clientKey}:vendor-part`]?.phase}
                            error={fieldSave[`${line.clientKey}:vendor-part`]?.error}
                          >
                            <Input
                              value={vendorPart}
                              onChange={(event) => {
                                markFieldDirty(`${line.clientKey}:vendor-part`);
                                setLines((current) =>
                                  current.map((row) =>
                                    row.clientKey === line.clientKey
                                      ? { ...row, extracted_vendor_part_number: event.target.value }
                                      : row,
                                  ),
                                );
                              }}
                              placeholder="Vendor catalog number"
                              aria-label="Vendor catalog number"
                              disabled={formLocked}
                              className="w-full"
                              onBlur={() => commitField(`${line.clientKey}:vendor-part`)}
                            />
                          </ApInvoiceFieldSaveFrame>
                        </div>
                        <div className="flex min-h-[8rem] min-w-0 flex-col md:min-h-0">
                          <ApInvoiceFieldSaveFrame
                            fieldKey={`${line.clientKey}:description`}
                            label="Description"
                            phase={fieldSave[`${line.clientKey}:description`]?.phase}
                            error={fieldSave[`${line.clientKey}:description`]?.error}
                          >
                          <textarea
                            value={line.description}
                            onChange={(event) => {
                              markFieldDirty(`${line.clientKey}:description`);
                              setLines((current) =>
                                current.map((row) =>
                                  row.clientKey === line.clientKey
                                    ? { ...row, description: event.target.value }
                                    : row,
                                ),
                              );
                            }}
                            onBlur={() => commitField(`${line.clientKey}:description`)}
                            placeholder="Description"
                            aria-label="Line description"
                            disabled={formLocked}
                            className={LINE_DESCRIPTION_CLASS}
                          />
                          </ApInvoiceFieldSaveFrame>
                        </div>
                        <div className="flex min-w-0 flex-col gap-2">
                          <div className="flex justify-end">
                            <button
                              type="button"
                              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-700"
                              aria-label="Remove line"
                              onClick={() => {
                                const next = lines.filter((row) => row.clientKey !== line.clientKey);
                                setLines(next);
                                stageDraft({ lines: next });
                              }}
                              disabled={formLocked}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </button>
                          </div>
                          <ApInvoiceFieldSaveFrame
                            fieldKey={`${line.clientKey}:qty`}
                            label="Qty"
                            phase={fieldSave[`${line.clientKey}:qty`]?.phase}
                            error={fieldSave[`${line.clientKey}:qty`]?.error}
                          >
                            <Input
                              type="number"
                              min="0"
                              step="any"
                              value={line.quantity}
                              onChange={(event) => {
                                markFieldDirty(`${line.clientKey}:qty`);
                                setLines((current) =>
                                  current.map((row) =>
                                    row.clientKey === line.clientKey
                                      ? { ...row, quantity: event.target.value }
                                      : row,
                                  ),
                                );
                              }}
                              onBlur={() => commitField(`${line.clientKey}:qty`)}
                              className="w-full px-2 text-right"
                              aria-label="Line quantity"
                              disabled={formLocked}
                            />
                          </ApInvoiceFieldSaveFrame>
                          <ApInvoiceFieldSaveFrame
                            fieldKey={`${line.clientKey}:unit-price`}
                            label="Unit price"
                            phase={fieldSave[`${line.clientKey}:unit-price`]?.phase}
                            error={fieldSave[`${line.clientKey}:unit-price`]?.error}
                          >
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.unit_price}
                              onChange={(event) => {
                                markFieldDirty(`${line.clientKey}:unit-price`);
                                setLines((current) =>
                                  current.map((row) =>
                                    row.clientKey === line.clientKey
                                      ? { ...row, unit_price: event.target.value }
                                      : row,
                                  ),
                                );
                              }}
                              onBlur={() => commitField(`${line.clientKey}:unit-price`)}
                              className="w-full px-2 text-right"
                              aria-label="Line unit price"
                              disabled={formLocked}
                            />
                          </ApInvoiceFieldSaveFrame>
                          <div>
                            <ApInvoiceDetailFieldLabel label="Line total" />
                            <p className="min-h-10 rounded-md border border-transparent px-2 py-2 text-right text-sm tabular-nums text-slate-700">
                              {lineTotalDisplay(line.quantity, line.unit_price)}
                            </p>
                          </div>
                        </div>
                      </div>
                      {skuTranslation != null ? (
                        <p
                          className={
                            skuAlert
                              ? "mt-3 flex items-start gap-1 text-xs font-semibold text-rose-800"
                              : "mt-3 text-xs text-slate-600"
                          }
                          role={skuAlert ? "alert" : undefined}
                        >
                          {skuAlert ? (
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                          ) : null}
                          {skuTranslation}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {headerTotal != null ? (
            <p className="text-right text-sm font-medium text-slate-900">
              Lines plus tariff, shipping, and fees:{" "}
              {headerTotal.toLocaleString(undefined, {
                style: "currency",
                currency: supplierCurrency,
                maximumFractionDigits: 2,
              })}
            </p>
          ) : null}
        </CardBody>
      </Card>
      )}

      {aiIssueReportSaved ? (
        <p className="shrink-0 text-sm text-slate-600">
          {aiIssueReportTicketId != null
            ? `Support ticket #${aiIssueReportTicketId} opened.`
            : "Support ticket opened."}
        </p>
      ) : null}

      {invoiceQuery.data != null && showTshWorkflowPanel ? (
        <div className="shrink-0">
          <ApInvoiceTshWorkflowPanel
            invoiceId={invoiceId}
            invoice={invoiceQuery.data}
            onUpdated={handleInvoiceUpdated}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <HubWorkspacePage>
      <HubWorkspaceHeader className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <PageTitleWithHelp hoverHint={EDIT_HELP_TEXT} className="mb-0" breadcrumbLabel={pageTitle}>
            {pageTitle}
          </PageTitleWithHelp>
          <Badge tone={apInvoiceWorkflowBadgeTone(workflowStatus)}>{invoiceQuery.data?.status_label ?? "Draft"}</Badge>
          {invoiceQuery.data?.ai_review_error != null && invoiceQuery.data.ai_review_error !== "" ? (
            <HoverHintTrigger
              trigger={
                <Badge tone="danger" className="cursor-help">
                  AI review error
                </Badge>
              }
            >
              {invoiceQuery.data.ai_review_error}
            </HoverHintTrigger>
          ) : null}
          {invoiceQuery.data?.freshdesk_ticket_id != null && invoiceQuery.data.freshdesk_ticket_id > 0 ? (
            <ApInvoiceFreshdeskTicketLink ticketId={invoiceQuery.data.freshdesk_ticket_id} variant="chip" />
          ) : null}
        </div>
        <div className="flex shrink-0 flex-nowrap items-center justify-end gap-2">
          {invoiceHeaderSaveStatus}
          {invoiceBackAction}
          {invoiceForwardAction}
          <ApInvoiceHeaderOverflowMenu items={overflowItems} />
        </div>
      </HubWorkspaceHeader>

      {returnToDraftConfirmOpen
        ? createPortal(
            <div {...hubModalOverlayProps(true)}>
              <Card className="mt-6 w-full max-w-md shadow-xl">
                <CardHeader className="border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Return this invoice to draft?</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Megaventory does not have a GIPI for this invoice yet, so you can edit it and submit again. Line
                    items and uploaded documents stay.
                  </p>
                </CardHeader>
                <CardBody className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={returnToDraftMutation.isPending}
                    onClick={() => setReturnToDraftConfirmOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={returnToDraftMutation.isPending}
                    isLoading={returnToDraftMutation.isPending}
                    onClick={() => returnToDraftMutation.mutate()}
                  >
                    Return to draft
                  </Button>
                </CardBody>
              </Card>
            </div>,
            document.body,
          )
        : null}

      {mergeOpen ? (
        <ApInvoiceMergeModal
          invoiceId={invoiceId}
          pending={mergeMutation.isPending}
          onCancel={() => {
            if (mergeMutation.isPending) return;
            setMergeOpen(false);
          }}
          onConfirm={(sourceInvoiceId) => mergeMutation.mutate(sourceInvoiceId)}
        />
      ) : null}

      {aiIssueReportOpen
        ? createPortal(
            <div {...hubModalOverlayProps(true)}>
              <Card className="mt-6 w-full max-w-lg shadow-xl">
                <CardHeader className="border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Report issues with this AI review</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Describe what the extraction got wrong. This opens a hub support ticket with the invoice and the
                    fields the review filled in, so the prompt can be corrected from the example.
                  </p>
                </CardHeader>
                <CardBody className="space-y-4">
                  <div>
                    <Label htmlFor="ap-invoice-ai-issue-report">What went wrong?</Label>
                    <TextArea
                      id="ap-invoice-ai-issue-report"
                      className="mt-1.5 font-sans"
                      maxLength={4000}
                      value={aiIssueReportText}
                      placeholder="For example: this file is a packing slip, not an invoice, and the vendor and lines were left blank."
                      onChange={(event) => setAiIssueReportText(event.target.value)}
                    />
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={reportAiIssueMutation.isPending}
                      onClick={() => setAiIssueReportOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      disabled={reportAiIssueMutation.isPending || aiIssueReportText.trim() === ""}
                      isLoading={reportAiIssueMutation.isPending}
                      onClick={() => reportAiIssueMutation.mutate(aiIssueReportText.trim())}
                    >
                      Submit report
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>,
            document.body,
          )
        : null}

      {discardConfirmOpen
        ? createPortal(
            <div {...hubModalOverlayProps(true)}>
              <Card className="mt-6 w-full max-w-md shadow-xl">
                <CardHeader className="border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Discard draft invoice?</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    This removes the draft, its line items, and any uploaded vendor invoice document. This cannot be
                    undone.
                  </p>
                </CardHeader>
                <CardBody className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={discardMutation.isPending}
                    onClick={() => setDiscardConfirmOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    disabled={discardMutation.isPending}
                    isLoading={discardMutation.isPending}
                    onClick={() => discardMutation.mutate()}
                  >
                    Discard draft
                  </Button>
                </CardBody>
              </Card>
            </div>,
            document.body,
          )
        : null}

      <HubWorkspaceBody className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(20rem,52%)_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:items-stretch lg:overflow-hidden">
        <HubWorkspacePanel scroll="hidden" className="min-h-0 min-w-0 pb-1">
          <ApInvoiceDocumentPanel
            invoiceId={invoiceId}
            layout="fill"
            file={invoiceFile}
            previewUrl={previewUrl}
            filename={invoiceFile?.name ?? serverFilename}
            contentType={serverContentType}
            emailPreviewHtml={emailPreviewHtml}
            documents={documents}
            selectedDocumentId={selectedDocumentId}
            onSelectDocument={setSelectedDocumentId}
            freshdeskTicketId={invoiceQuery.data?.freshdesk_ticket_id}
            onFileChange={handleFileChange}
            onRemove={handleRemoveDocument}
            onAiReview={() => retryAiReviewMutation.mutate()}
            showAiReviewButton={showAiReviewButton && !aiReviewInProgress}
            isAiReviewStarting={retryAiReviewMutation.isPending}
            disabled={uploadMutation.isPending || deleteDocumentMutation.isPending || formLocked}
            isUploading={uploadMutation.isPending}
            splitSuggestionBadge={splitSuggestionBadge}
          />
        </HubWorkspacePanel>
        <HubWorkspacePanel scroll={splitModalOpen ? "hidden" : "auto"} className="min-h-0 min-w-0">
          {splitModalOpen ? (
            <ApInvoiceSplitConfirmModal
              open={splitModalOpen}
              mode={splitModalMode}
              invoiceId={invoiceId}
              documents={documents}
              draftDefaults={splitDraftDefaults}
              selectedDocumentId={selectedDocumentId}
              onSelectDocument={setSelectedDocumentId}
              onDiscardDocument={() => undefined}
              discardingDocumentId={
                deleteDocumentMutation.isPending ? (deleteDocumentMutation.variables ?? null) : null
              }
              isPending={splitVendorMutation.isPending || migrateManufacturerBatchMutation.isPending}
              errorMessage={splitDialogError}
              onCancel={() => {
                setSplitDialogError(null);
                setSplitModalOpen(false);
              }}
              onConfirm={async (groups, discardDocumentIds) => {
                setSplitDialogError(null);
                setSplitBusy(true);
                await queryClient.cancelQueries({ queryKey: ["accounting", "ap-invoice", invoiceId] });
                try {
                  for (const documentId of discardDocumentIds) {
                    await deleteDocumentMutation.mutateAsync(documentId);
                  }
                } catch {
                  setSplitBusy(false);
                  return;
                }
                if (splitModalMode === "vendor") {
                  splitVendorMutation.mutate(groups);
                } else {
                  migrateManufacturerBatchMutation.mutate(groups);
                }
              }}
            />
          ) : (
            invoiceFormSection
          )}
        </HubWorkspacePanel>
      </HubWorkspaceBody>
    </HubWorkspacePage>
  );
}

/** @deprecated Use ApInvoiceNewPage and ApInvoiceEditPage */
export const ApInvoiceCreatePage = ApInvoiceNewPage;
